import { Meteor } from 'meteor/meteor';
import { Session } from 'meteor/session';
import { Template } from 'meteor/templating';
import { ReactiveVar } from 'meteor/reactive-var';
import { Tracker } from 'meteor/tracker';
import { Accounts } from 'meteor/accounts-base';
import { FlowRouter } from 'meteor/ostrio:flow-router-extra';

import {
  DIFFUSION_NODE_ID,
  DiffusionAliases,
  DiffusionAccessPolicies,
  DiffusionNodeState,
  DiffusionSceneAccess,
  DiffusionVersions,
  Cartes,
  HubFiles,
  HypeWidgets,
  Projets,
  Scenes,
  url_asset_diffusion_protegee,
} from '../imports/collections.js';
import {
  normalise_slug_public_diffusion,
} from '../imports/diffusion_public_aliases.js';
import {
  normalise_catalogue_diapos_taille_pct,
} from '../imports/projet_publications_settings.js';
import {
  est_runtime_embarque,
  demarre_runtime_embarque,
  runtime_embarque_pret,
  version_id_embarque,
  scene_verrouillee_embarque,
} from './imports/runtime_embarque.js';
import { ouvre_achat_embarque } from './imports/achat_embarque/achat_embarque.js';

import './index.html';
import './index.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import {
  create_lecture_audio_player,
} from './imports/lecture_audio/lecture_audio.js';
import './imports/audio_entry_gate/lecture/audio_entry_gate_lecture.js';
import {
  arrete_lecture_scene_courante,
  demarre_lecture_scene_courante,
  deverrouille_audio_instant_lecture_scene,
  ouvre_scene_lecture_avec_fondu,
  prechauffe_assets_scene_lecture_diffusion,
} from './scene/scene.js';
import './hub_scene_overrides.css';
import {
  PIXEL_META_TEXTE_BANDEAU_DEFAUT,
  PIXEL_META_TEXTE_OPTIN_SHOPIFY_DEFAUT,
} from '../imports/projet_communication_settings.js';

const LECTURE_SVG_NS = 'http://www.w3.org/2000/svg';
const LECTURE_BUBBLES_LAYER_ID = 'diffusion_lecture_bubbles';
const LECTURE_STYLE_ID = 'diffusion_lecture_styles';
const LECTURE_HOVER_SCALE = 1.035;
const LECTURE_CARD_MARGIN = 0.08;
const LECTURE_BUBBLE_PRESS_MS = 520;
const USER_SESSION_KEY = 'diffusion.user_session';
const gCurrentUser = new ReactiveVar (null);
const gAuthReady = new ReactiveVar (false);
const gCatalogueReady = new ReactiveVar (false);
const gVersionDataReady = new ReactiveVar (false);
const gAccessState = new ReactiveVar ({authorized : false, limited : false});
const gAccessGateOpen = new ReactiveVar (false);
const gAccessGateRendered = new ReactiveVar (false);
const gAccessGateClosing = new ReactiveVar (false);
const gAccessGateForm = new ReactiveVar (false);
const gAccessGateBusy = new ReactiveVar (false);
const gAccessGateMessage = new ReactiveVar (null);
const gAccessGateTitle = new ReactiveVar (null);
const gDiffusionToast = new ReactiveVar (null);
const gPasswordlessSentDialog = new ReactiveVar (false);
const gWelcomeOverlay = new ReactiveVar (null);
const gWelcomeOverlayClosing = new ReactiveVar (false);
const gAccountOverlayRendered = new ReactiveVar (false);
const gAccountOverlayClosing = new ReactiveVar (false);
let gAccountOverlayCloseTimer = null;
let gAccessGateCloseTimer = null;
let gDiffusionToastTimer = null;
let gWelcomeOverlayCloseTimer = null;
let gPasswordlessReturnPath = null;
let gPasswordlessVersionId = null;
let gPasswordlessLoginKey = null;
let gAuthSuccessMessage = null;

function document_svg_depuis_object_lecteur (pObject)
  {
    if (!pObject) return null;
    try
      {
        const tDoc = pObject.contentDocument
          || (typeof pObject.getSVGDocument === 'function' ? pObject.getSVGDocument () : null);
        const tRoot = tDoc && tDoc.documentElement;
        if (!tRoot || !tRoot.tagName || String (tRoot.tagName).toLowerCase () !== 'svg')
          return null;
        return tDoc;
      }
    catch (e)
      {
        return null;
      }
  }

function version_id_route ()
  {
    return Session.get ('version_id');
  }

function alias_public_route ()
  {
    return normalise_slug_public_diffusion (Session.get ('public_slug'));
  }

function resout_alias_public_route ()
  {
    const tSlug = alias_public_route ();
    if (!tSlug) return null;
    const tAlias = DiffusionAliases.findOne ({slug : tSlug, active : true});
    if (!tAlias || !tAlias.version_id) return null;
    return tAlias;
  }

function slug_public_route_pour_version (pVersionId)
  {
    const tVersionId = typeof pVersionId === 'string' ? pVersionId.trim () : '';
    if (!tVersionId) return '';
    const tSlug = alias_public_route ();
    const tAlias = tSlug && resout_alias_public_route ();
    return tAlias && tAlias.version_id === tVersionId ? tSlug : '';
  }

function slug_public_actif_version (pVersionId)
  {
    const tVersionId = typeof pVersionId === 'string' ? pVersionId.trim () : '';
    if (!tVersionId) return '';
    const tAlias = DiffusionAliases.findOne ({version_id : tVersionId, active : true});
    return normalise_slug_public_diffusion (tAlias && tAlias.slug);
  }

function version_id_alias_public_local (pSlug)
  {
    const tSlug = normalise_slug_public_diffusion (pSlug);
    if (!tSlug) return null;
    const tAlias = DiffusionAliases.findOne ({slug : tSlug, active : true});
    return tAlias && tAlias.version_id || null;
  }

function version_courante ()
  {
    const tVersionId = version_id_route ();
    return tVersionId ? DiffusionVersions.findOne (tVersionId) : null;
  }

function route_mode_test_diffusion ()
  {
    try
      {
        return FlowRouter.getQueryParam ('cogi_test') === '1'
          || new URLSearchParams (window.location.search || '').get ('cogi_test') === '1';
      }
    catch (e)
      {
        return false;
      }
  }

// Lecteur alternatif Pixi (essai) : le paramètre ?lecteur=pixi remplace le
// template `scene` (SVG inline) par `scene_pixi` (WebGL). Le paramètre est
// conservé par query_string_route_courante lors des changements de scène.
// Dans l'appli embarquée (sans URL à régler), le réglage public
// `lecteur: "pixi"` joue le même rôle (appli/build_app.sh --pixi).
function lecteur_pixi_route ()
  {
    try
      {
        return Meteor.settings?.public?.lecteur === 'pixi'
          || FlowRouter.getQueryParam ('lecteur') === 'pixi'
          || new URLSearchParams (window.location.search || '').get ('lecteur') === 'pixi';
      }
    catch (e)
      {
        return false;
      }
  }

// Le lecteur Pixi (pixi.js + scene_pixi) est chargé à la demande : le mode
// normal ne télécharge ni ne compile rien de lui. Chargement lancé dès
// l'ouverture si l'URL le demande, en parallèle du démarrage de Meteor.
const gLecteurPixiCharge = new ReactiveVar (false);
let gLecteurPixiPromesse = null;

function charge_lecteur_pixi ()
  {
    if (!gLecteurPixiPromesse)
      gLecteurPixiPromesse = import ('./scene_pixi/scene_pixi.js')
        .then (() => gLecteurPixiCharge.set (true))
        .catch ((pErr) =>
          {
            gLecteurPixiPromesse = null;
            console.error ('[diffusion] chargement du lecteur Pixi impossible :', pErr);
          });
    return gLecteurPixiPromesse;
  }

if (lecteur_pixi_route ()) charge_lecteur_pixi ();

function garde_mode_test_diffusion ()
  {
    return Session.get ('diffusion_mode_test') === true
      || route_mode_test_diffusion ();
  }

function query_string_route_courante ()
  {
    try
      {
        const tQuery = FlowRouter.current && FlowRouter.current () && FlowRouter.current ().queryParams;
        const tParams = new URLSearchParams ();
        Object.keys (tQuery || {}).forEach ((pCle) =>
          {
            const tValeur = tQuery[pCle];
            if (Array.isArray (tValeur))
              tValeur.forEach ((pValeur) => tParams.append (pCle, pValeur));
            else if (tValeur !== undefined && tValeur !== null)
              tParams.set (pCle, tValeur);
          });
        const tTexte = tParams.toString ();
        if (tTexte) return '?' + tTexte;
      }
    catch (e) {}
    return (typeof window !== 'undefined' && window.location && window.location.search) || '';
  }

function chemin_scene_lecteur_diffusion (pVersionId, pSlug)
  {
    const tVersionId = typeof pVersionId === 'string' ? pVersionId.trim () : '';
    const tSlug = typeof pSlug === 'string' ? pSlug.trim () : '';
    if (!tVersionId || !tSlug) return null;
    const tSlugPublic = slug_public_route_pour_version (tVersionId);
    const tBase = tSlugPublic
      ? `/${encodeURIComponent (tSlugPublic)}`
      : `/jouer/${encodeURIComponent (tVersionId)}`;
    return `${tBase}/${encodeURIComponent (tSlug)}${query_string_route_courante ()}`;
  }

function chemin_entree_lecteur_diffusion (pVersionId)
  {
    const tVersionId = typeof pVersionId === 'string' ? pVersionId.trim () : '';
    if (!tVersionId) return '#';
    const tSlugPublic = slug_public_actif_version (tVersionId);
    if (tSlugPublic) return `/${encodeURIComponent (tSlugPublic)}`;
    return `/jouer/${encodeURIComponent (tVersionId)}`;
  }

function chemin_entree_passwordless_diffusion (pVersionId, pFallback = '/')
  {
    const tVersionId = typeof pVersionId === 'string' ? pVersionId.trim () : '';
    return tVersionId
      ? `/jouer/${encodeURIComponent (tVersionId)}`
      : pFallback;
  }

function chemin_local_diffusion_valide (pChemin, pFallback = '/')
  {
    const tChemin = typeof pChemin === 'string' ? pChemin.trim () : '';
    if (!tChemin || !tChemin.startsWith ('/') || tChemin.startsWith ('//') || tChemin.includes ('://'))
      return pFallback;
    if (/^\/auth\/passwordless(?:\/|$)/.test (tChemin)) return pFallback;
    return tChemin;
  }

function chemin_retour_passwordless_courant ()
  {
    if (typeof window === 'undefined' || !window.location) return '/';
    return chemin_local_diffusion_valide (
      (window.location.pathname || '/')
        + (window.location.search || '')
        + (window.location.hash || ''),
      '/'
    );
  }

function retour_passwordless_initial ()
  {
    if (typeof window === 'undefined' || !window.location) return null;
    try
      {
        const tParams = new URL (window.location.href).searchParams;
        if (!tParams.get ('loginToken')) return null;
        gPasswordlessVersionId = (tParams.get ('version_id') || '').trim () || null;
        return chemin_entree_passwordless_diffusion (
          gPasswordlessVersionId,
          chemin_local_diffusion_valide (
            tParams.get ('retour') || tParams.get ('return_path') || '/',
            '/'
          )
        );
      }
    catch (e)
      {
        return null;
      }
  }

gPasswordlessReturnPath = retour_passwordless_initial ();

// Paramètres de l'URL d'atterrissage (utm_source, etc.) capturés au chargement
// de la page : ils sont joints à la demande de lien de connexion pour être
// enregistrés dans la fiche du nouvel utilisateur (analyse marketing).
function parametres_url_courants_diffusion ()
  {
    const tParams = {};
    if (typeof window === 'undefined' || !window.location) return tParams;
    try
      {
        new URLSearchParams (window.location.search || '').forEach ((pValeur, pCle) =>
          {
            const tCle = String (pCle || '').trim ();
            if (!tCle || tCle === 'loginToken') return;
            tParams[tCle] = String (pValeur);
          });
      }
    catch (e) {}
    return tParams;
  }

const gUrlParamsAtterrissage = parametres_url_courants_diffusion ();

function parametres_url_inscription_diffusion ()
  {
    // Les paramètres d'atterrissage priment sur ceux de l'URL courante :
    // c'est la provenance initiale qui intéresse le marketing.
    return {...parametres_url_courants_diffusion (), ...gUrlParamsAtterrissage};
  }

const TITRE_SESSION_CONNEXION_EXPIREE = 'Session de connexion expirée, merci de vous reconnecter';

function affiche_notification_diffusion (pTexte, pType = 'succes', pOptions = {})
  {
    const tTexte = typeof pTexte === 'string' ? pTexte.trim () : '';
    if (!tTexte) return;
    const tDurationMs = Math.max (1200, Number (pOptions.duration_ms) || 4500);
    if (gDiffusionToastTimer)
      {
        clearTimeout (gDiffusionToastTimer);
        gDiffusionToastTimer = null;
      }
    gDiffusionToast.set ({
      texte : tTexte,
      type : pType === 'erreur' ? 'erreur' : 'succes',
      duration_ms : tDurationMs,
    });
    gDiffusionToastTimer = setTimeout (() =>
      {
        gDiffusionToastTimer = null;
        gDiffusionToast.set (null);
      }, tDurationMs);
  }

function affiche_dialogue_passwordless_envoye ()
  {
    gPasswordlessSentDialog.set (true);
  }

function affiche_overlay_bienvenue_diffusion (pHtml)
  {
    const tHtml = typeof pHtml === 'string' ? pHtml.trim () : '';
    if (!tHtml) return;
    if (gWelcomeOverlayCloseTimer)
      {
        clearTimeout (gWelcomeOverlayCloseTimer);
        gWelcomeOverlayCloseTimer = null;
      }
    gWelcomeOverlayClosing.set (false);
    gWelcomeOverlay.set ({html : tHtml});
  }

function ferme_overlay_bienvenue_diffusion ()
  {
    if (!gWelcomeOverlay.get () || gWelcomeOverlayClosing.get ()) return;
    gWelcomeOverlayClosing.set (true);
    gWelcomeOverlayCloseTimer = setTimeout (() =>
      {
        gWelcomeOverlayCloseTimer = null;
        gWelcomeOverlay.set (null);
        gWelcomeOverlayClosing.set (false);
      }, 420);
  }

if (typeof window !== 'undefined')
  {
    window.cogibulleDiffusionCheminScene = function (pScene)
      {
        const tScene = pScene && typeof pScene === 'object' ? pScene : null;
        const tSlug = tScene && tScene.slug;
        const tVersionId = tScene && tScene._diffusion_version_id
          || Session.get ('diffusion_version_id')
          || version_id_route ();
        return chemin_scene_lecteur_diffusion (tVersionId, tSlug);
      };
  }

Tracker.autorun (() =>
  {
    if (typeof document === 'undefined' || !document.body) return;
    document.body.classList.toggle ('diffusion-test-mode', Session.get ('diffusion_mode_test') === true);
  });

function projet_courant ()
  {
    const tVersionId = version_id_route ();
    return tVersionId ? Projets.findOne ({_diffusion_version_id : tVersionId}) : null;
  }

function communication_diffusion_courante ()
  {
    const tProjet = projet_courant ();
    if (tProjet && tProjet.communication_diffusion) return tProjet.communication_diffusion;
    const tVersion = DiffusionVersions.findOne (
      {active : true, status : 'active'},
      {sort : {published_at : -1, game_slug : 1}}
    );
    const tProjetCatalogue = tVersion
      ? Projets.findOne ({_diffusion_version_id : tVersion._id})
      : null;
    return (tProjetCatalogue && tProjetCatalogue.communication_diffusion) || {};
  }

function version_id_entree_jeu_defaut ()
  {
    const tVersion = DiffusionVersions.findOne (
      {active : true, status : 'active'},
      {sort : {published_at : -1, game_slug : 1}}
    );
    return tVersion && tVersion._id || '';
  }

function logo_communication_diffusion_url ()
  {
    const tCommunication = communication_diffusion_courante ();
    const tLogoUrl = typeof (tCommunication && tCommunication.logo_url) === 'string'
      ? tCommunication.logo_url.trim ()
      : '';
    if (tLogoUrl) return url_asset_diffusion_protegee (tLogoUrl, version_id_route ());

    const tLogoFileId = typeof (tCommunication && tCommunication.logo_file_id) === 'string'
      ? tCommunication.logo_file_id.trim ()
      : '';
    if (!tLogoFileId) return null;

    const tFichier = HubFiles.findOne (tLogoFileId);
    return HubFiles.link (tFichier);
  }

function image_partage_communication_diffusion_url ()
  {
    const tCommunication = communication_diffusion_courante ();
    const tShare = tCommunication && tCommunication.share || {};
    const tImageUrl = typeof tShare.image_url === 'string'
      ? tShare.image_url.trim ()
      : '';
    if (tImageUrl) return url_asset_diffusion_protegee (tImageUrl, version_id_route ());

    const tImageFileId = typeof tShare.image_file_id === 'string'
      ? tShare.image_file_id.trim ()
      : '';
    if (!tImageFileId) return null;

    const tFichier = HubFiles.findOne (tImageFileId);
    return HubFiles.link (tFichier);
  }

function affichage_communication_courant ()
  {
    const tCommunication = communication_diffusion_courante ();
    return (tCommunication && tCommunication.header) || {};
  }

function html_message_passwordless_envoye ()
  {
    const tHtml = communication_diffusion_courante ().passwordless_sent_html;
    return typeof tHtml === 'string' && tHtml.trim ()
      ? tHtml
      : '<p>Merci, un e-mail vient vient de vous être envoyé avec un lien de connexion directe au jeu. Vous pouvez refermer cette fenêtre.</p>';
  }

Template.registerHelper ('communication_logo_url_diffusion', () => logo_communication_diffusion_url ());

// Helpers globaux : la case d'opt-in prospection apparaît dans les deux
// formulaires d'e-mail (connexion et identification), templates distincts.
Template.registerHelper ('optin_prospection_visible', () =>
  {
    const tReglages = reglages_pixel_meta_courants ();
    return !!(tReglages && tReglages.shopify_actif);
  });

Template.registerHelper ('texte_optin_prospection', () =>
  {
    const tReglages = reglages_pixel_meta_courants ();
    return (tReglages && tReglages.texte_optin_shopify) || PIXEL_META_TEXTE_OPTIN_SHOPIFY_DEFAUT;
  });

Template.registerHelper ('optin_prospection_precoche_attr', () =>
  {
    const tReglages = reglages_pixel_meta_courants ();
    return tReglages && tReglages.optin_precoche ? 'checked' : null;
  });

// ---------------------------------------------------------------------------
// Pixel Meta + bandeau de consentement cookies (conformité CNIL)
// Le Pixel ne se charge qu'après consentement explicite de l'utilisateur,
// et jamais dans le runtime embarqué. Les réglages viennent du hub
// (inspecteur projet, section Communication → Suivi Meta) et voyagent dans
// communication_diffusion.pixel.
// ---------------------------------------------------------------------------

const CONSENTEMENT_COOKIES_KEY = 'diffusion.consentement_cookies';
// Opt-in prospection (case Shopify du bandeau) : consentement spécifique,
// distinct du consentement de mesure, jamais pré-coché.
const OPTIN_PROSPECTION_KEY = 'diffusion.optin_prospection';

function lecture_consentement_cookies ()
  {
    if (typeof localStorage === 'undefined') return '';
    const tValeur = localStorage.getItem (CONSENTEMENT_COOKIES_KEY);
    return tValeur === 'accepte' || tValeur === 'refuse' ? tValeur : '';
  }

const gConsentementCookies = new ReactiveVar (lecture_consentement_cookies ());
// Dialogue de purge des données locales (raccourci Ctrl+Option+L).
const gDialogueEffaceLocalOuvert = new ReactiveVar (false);
const gEvenementsPixelEnAttente = [];
let gPixelMetaChargeId = '';

function enregistre_consentement_cookies (pChoix)
  {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem (CONSENTEMENT_COOKIES_KEY, pChoix);
    gConsentementCookies.set (pChoix);
  }

function enregistre_optin_prospection (pAccepte)
  {
    if (typeof localStorage !== 'undefined')
      localStorage.setItem (OPTIN_PROSPECTION_KEY, pAccepte ? 'accepte' : 'refuse');
  }

function optin_prospection_accepte ()
  {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem (OPTIN_PROSPECTION_KEY) === 'accepte';
  }

function reglages_pixel_meta_courants ()
  {
    if (est_runtime_embarque ()) return null;
    const tPixel = communication_diffusion_courante ().pixel;
    if (!tPixel || typeof tPixel !== 'object' || tPixel.actif !== true) return null;
    const tPixelId = typeof tPixel.pixel_id === 'string' ? tPixel.pixel_id.trim () : '';
    if (!tPixelId) return null;
    return {
      pixel_id : tPixelId,
      evenement_inscription : tPixel.evenement_inscription !== false,
      evenement_lancement : tPixel.evenement_lancement !== false,
      evenement_lead : tPixel.evenement_lead !== false,
      consentement_obligatoire : tPixel.consentement_obligatoire === true,
      // Tests marketing temporaires : bandeau masqué, suivi actif d'office.
      sans_consentement : tPixel.sans_consentement === true,
      texte_bandeau : typeof tPixel.texte_bandeau === 'string' ? tPixel.texte_bandeau.trim () : '',
      shopify_actif : tPixel.shopify_actif === true,
      texte_optin_shopify : typeof tPixel.texte_optin_shopify === 'string' ? tPixel.texte_optin_shopify.trim () : '',
      optin_precoche : tPixel.optin_precoche === true,
    };
  }

// Consentement effectif : en mode « suivi sans consentement » (bascule du hub,
// tests marketing), le suivi est actif d'office sans rien enregistrer dans le
// navigateur ; au retour en mode normal, le choix réel de l'utilisateur (ou son
// absence, qui réaffiche le bandeau) reprend la main.
function consentement_cookies_effectif ()
  {
    const tReglages = reglages_pixel_meta_courants ();
    if (tReglages && tReglages.sans_consentement) return 'accepte';
    return gConsentementCookies.get ();
  }

function cookie_navigateur_diffusion (pNom)
  {
    if (typeof document === 'undefined') return '';
    const tMatch = document.cookie.match (new RegExp ('(?:^|; )' + pNom + '=([^;]*)'));
    return tMatch ? decodeURIComponent (tMatch[1]) : '';
  }

function fbc_courant_diffusion ()
  {
    const tFbc = cookie_navigateur_diffusion ('_fbc');
    if (tFbc) return tFbc;
    // Reconstruction depuis fbclid (format documenté par Meta) si le cookie
    // n'a pas encore été posé.
    try
      {
        const tFbclid = new URLSearchParams (window.location.search).get ('fbclid');
        if (tFbclid) return 'fb.1.' + Date.now () + '.' + tFbclid;
      }
    catch (e) { /* URL illisible : pas de fbc */ }
    return '';
  }

function declenche_lead_pixel_diffusion (pEmail, pVersionId)
  {
    const tReglages = reglages_pixel_meta_courants ();
    if (!tReglages || !tReglages.evenement_lead) return;
    const tVersionId = pVersionId || version_id_route () || '';
    Meteor.call (
      'diffusion.pixel.lead',
      {
        version_id : tVersionId,
        email : pEmail,
        consentement : gConsentementCookies.get () || '',
        optin_prospection : optin_prospection_accepte (),
        fbp : cookie_navigateur_diffusion ('_fbp'),
        fbc : fbc_courant_diffusion (),
        page_url : window.location.href,
      },
      (pErr, pRes) =>
        {
          if (pErr || !pRes || !pRes.event_id) return;
          const tProjet = projet_courant ();
          evenement_pixel_meta (
            'Lead',
            {source : (tProjet && tProjet.slug) || 'diffusion'},
            {event_id : pRes.event_id}
          );
        }
    );
  }

function texte_bandeau_cookies_courant ()
  {
    const tReglages = reglages_pixel_meta_courants ();
    if (tReglages && tReglages.texte_bandeau) return tReglages.texte_bandeau;
    // Texte par défaut : la phrase de réassurance n'a de sens que si le refus
    // est réellement sans conséquence (mode consentement libre).
    if (tReglages && tReglages.consentement_obligatoire) return PIXEL_META_TEXTE_BANDEAU_DEFAUT;
    return PIXEL_META_TEXTE_BANDEAU_DEFAUT + " Refuser n'a aucune conséquence sur le jeu.";
  }

function injecte_script_pixel_meta ()
  {
    if (window.fbq) return;
    const tFbq = function () {
      tFbq.callMethod ? tFbq.callMethod.apply (tFbq, arguments) : tFbq.queue.push (arguments);
    };
    window.fbq = tFbq;
    if (!window._fbq) window._fbq = tFbq;
    tFbq.push = tFbq;
    tFbq.loaded = true;
    tFbq.version = '2.0';
    tFbq.queue = [];
    const tScript = document.createElement ('script');
    tScript.async = true;
    tScript.src = 'https://connect.facebook.net/en_US/fbevents.js';
    const tPremier = document.getElementsByTagName ('script')[0];
    if (tPremier && tPremier.parentNode) tPremier.parentNode.insertBefore (tScript, tPremier);
    else document.head.appendChild (tScript);
  }

function charge_pixel_meta (pPixelId)
  {
    if (gPixelMetaChargeId) return;
    injecte_script_pixel_meta ();
    if (!window.fbq) return;
    gPixelMetaChargeId = pPixelId;
    window.fbq ('init', pPixelId);
    window.fbq ('track', 'PageView');
    while (gEvenementsPixelEnAttente.length)
      {
        const tEvenement = gEvenementsPixelEnAttente.shift ();
        emet_fbq_track_diffusion (tEvenement.nom, tEvenement.params, tEvenement.event_id);
      }
  }

function emet_fbq_track_diffusion (pNom, pParams, pEventId)
  {
    if (!window.fbq) return;
    if (pEventId) window.fbq ('track', pNom, pParams, {eventID : pEventId});
    else window.fbq ('track', pNom, pParams);
  }

function evenement_pixel_meta (pNom, pParams = {}, pOptions = {})
  {
    const tReglages = reglages_pixel_meta_courants ();
    if (!tReglages) return;
    if (pNom === 'CompleteRegistration' && !tReglages.evenement_inscription) return;
    if (pNom === 'ViewContent' && !tReglages.evenement_lancement) return;
    if (pNom === 'Lead' && !tReglages.evenement_lead) return;
    if (consentement_cookies_effectif () === 'refuse') return;
    const tEventId = pOptions && pOptions.event_id || null;
    if (gPixelMetaChargeId && window.fbq && consentement_cookies_effectif () === 'accepte')
      {
        emet_fbq_track_diffusion (pNom, pParams, tEventId);
        return;
      }
    // Consentement pas encore exprimé : on retient l'événement pour le
    // rejouer si l'utilisateur accepte pendant la même page.
    gEvenementsPixelEnAttente.push ({nom : pNom, params : pParams, event_id : tEventId});
  }

Meteor.startup (() =>
  {
    Tracker.autorun (() =>
      {
        if (consentement_cookies_effectif () !== 'accepte') return;
        const tReglages = reglages_pixel_meta_courants ();
        if (tReglages) charge_pixel_meta (tReglages.pixel_id);
      });
  });

function couleur_hex_valide (pValeur)
  {
    const tValeur = typeof pValeur === 'string' ? pValeur.trim () : '';
    if (/^#[0-9a-f]{3}$/i.test (tValeur))
      {
        return '#' + tValeur.slice (1).split ('').map ((pChar) => pChar + pChar).join ('');
      }
    if (/^#[0-9a-f]{6}$/i.test (tValeur)) return tValeur;
    return '';
  }

function couleur_texte_lisible (pFond)
  {
    const tHex = couleur_hex_valide (pFond);
    if (!tHex) return '';
    const tRouge = parseInt (tHex.slice (1, 3), 16) / 255;
    const tVert = parseInt (tHex.slice (3, 5), 16) / 255;
    const tBleu = parseInt (tHex.slice (5, 7), 16) / 255;
    const tLinearise = (pCanal) => pCanal <= 0.03928
      ? pCanal / 12.92
      : Math.pow ((pCanal + 0.055) / 1.055, 2.4);
    const tLuminance = 0.2126 * tLinearise (tRouge)
      + 0.7152 * tLinearise (tVert)
      + 0.0722 * tLinearise (tBleu);
    return tLuminance > 0.48 ? '#18202a' : '#ffffff';
  }

function ajoute_variable_couleur_diffusion (pVars, pNom, pValeur)
  {
    const tFond = couleur_hex_valide (pValeur);
    if (!tFond) return;
    pVars.push (`--diffusion-${pNom}-bg: ${tFond}`);
    pVars.push (`--diffusion-${pNom}-text: ${couleur_texte_lisible (tFond)}`);
  }

// Fond de page de l'appli embarquée (chaîne vide hors mode embarqué) : le
// « Fond » de la scène courante (inspecteur de scène du hub) si elle en a un,
// sinon la couleur de page du projet, sinon noir — sur un téléphone, des bandes
// noires autour de la scène se fondent dans le cadre de l'appareil.
const FOND_PAGE_EMBARQUE_DEFAUT = '#000000';

function fond_scene_embarque ()
  {
    if (!est_runtime_embarque ()) return '';
    const tScene = scene_active ();
    const tHeader = affichage_communication_courant ();
    return couleur_hex_valide (tScene && tScene.mode_lecture_masque && tScene.mode_lecture_masque.fond)
      || couleur_hex_valide (tHeader.page_bg_light)
      || FOND_PAGE_EMBARQUE_DEFAUT;
  }

function applique_couleurs_page_diffusion ()
  {
    if (typeof document === 'undefined') return;
    const tRoot = document.documentElement;
    if (!tRoot || !tRoot.style) return;
    const tHeader = affichage_communication_courant ();
    const tCouleurs =
      {
        'browser-light' : tHeader.browser_bg_light,
        'browser-dark'  : tHeader.browser_bg_dark,
        'page-light' : tHeader.page_bg_light,
        'page-dark'  : tHeader.page_bg_dark,
      };
    // Appli embarquée : l'écran d'un téléphone est plus allongé que les scènes,
    // les bandes qui restent autour prennent le « Fond » choisi dans le hub
    // pour la scène courante (si elle en a un), sinon la couleur de page du projet.
    const tFondScene = fond_scene_embarque ();
    if (tFondScene)
      {
        tCouleurs['page-light'] = tFondScene;
        tCouleurs['page-dark'] = tFondScene;
        // La racine aussi : sur iPhone, la zone de l'indicateur d'accueil
        // déborde du cadre du lecteur et resterait blanche.
        tRoot.style.backgroundColor = tFondScene;
      }
    Object.entries (tCouleurs).forEach (([pNom, pValeur]) =>
      {
        const tFond = couleur_hex_valide (pValeur);
        const tBgVar = `--diffusion-${pNom}-bg`;
        const tTextVar = `--diffusion-${pNom}-text`;
        if (tFond)
          {
            tRoot.style.setProperty (tBgVar, tFond);
            tRoot.style.setProperty (tTextVar, couleur_texte_lisible (tFond));
          }
        else
          {
            tRoot.style.removeProperty (tBgVar);
            tRoot.style.removeProperty (tTextVar);
          }
      });
  }

function ouvre_overlay_compte_diffusion ()
  {
    if (gAccountOverlayCloseTimer)
      {
        clearTimeout (gAccountOverlayCloseTimer);
        gAccountOverlayCloseTimer = null;
      }
    gAccountOverlayClosing.set (false);
    gAccountOverlayRendered.set (true);
  }

function ferme_carte_active_diffusion ()
  {
    if (typeof window === 'undefined') return;
    if (typeof window.cogibulleDiffusionAnnuleOuFermeInteractionProtegee === 'function')
      window.cogibulleDiffusionAnnuleOuFermeInteractionProtegee ();
    else if (typeof window.cogibulleDiffusionFermeCarteActive === 'function')
      window.cogibulleDiffusionFermeCarteActive ();
  }

function ferme_overlay_compte_diffusion ()
  {
    if (!gAccountOverlayRendered.get () || gAccountOverlayClosing.get ()) return;
    gAccountOverlayClosing.set (true);
    gAccountOverlayCloseTimer = setTimeout (() =>
      {
        gAccountOverlayCloseTimer = null;
        gAccountOverlayRendered.set (false);
        gAccountOverlayClosing.set (false);
      }, 560);
  }

function ferme_interfaces_auth_diffusion_immediatement ()
  {
    if (gAccountOverlayCloseTimer)
      {
        clearTimeout (gAccountOverlayCloseTimer);
        gAccountOverlayCloseTimer = null;
      }
    if (gAccessGateCloseTimer)
      {
        clearTimeout (gAccessGateCloseTimer);
        gAccessGateCloseTimer = null;
      }
    if (gWelcomeOverlayCloseTimer)
      {
        clearTimeout (gWelcomeOverlayCloseTimer);
        gWelcomeOverlayCloseTimer = null;
      }
    gAccountOverlayRendered.set (false);
    gAccountOverlayClosing.set (false);
    gAccessGateOpen.set (false);
    gAccessGateRendered.set (false);
    gAccessGateClosing.set (false);
    gAccessGateForm.set (false);
    gAccessGateBusy.set (false);
    gAccessGateMessage.set (null);
    gPasswordlessSentDialog.set (false);
    gWelcomeOverlay.set (null);
    gWelcomeOverlayClosing.set (false);
  }

function hub_runtime_pret ()
  {
    const tVersionId = version_id_route ();
    if (!gVersionDataReady.get ()) return false;
    const tScene = scene_active ();
    const tSvgId = tScene && (tScene.svg_runtime_id || tScene.svg_id);
    return !!(
      tVersionId
      && Projets.find ({_diffusion_version_id : tVersionId}).count ()
      && Scenes.find ({_diffusion_version_id : tVersionId}).count ()
      && (!tSvgId || HubFiles.findOne ({_id : tSvgId}))
    );
  }

function policy_courante ()
  {
    const tVersionId = version_id_route ();
    return tVersionId ? DiffusionAccessPolicies.findOne (tVersionId) : null;
  }

function cle_session_locale (pVersionId)
  {
    return 'diffusion.session.' + pVersionId;
  }

function user_session_id ()
  {
    return Session.get (USER_SESSION_KEY) || localStorage.getItem (USER_SESSION_KEY) || null;
  }

function ecrit_cookie_session_diffusion (pSessionId)
  {
    const tSessionId = String (pSessionId || "").trim ();
    if (!tSessionId) return;
    document.cookie = "diffusion_user_session_id="
      + encodeURIComponent (tSessionId)
      + "; Path=/; Max-Age=" + (14 * 24 * 60 * 60)
      + "; SameSite=Lax";
  }

function efface_cookie_session_diffusion ()
  {
    document.cookie = "diffusion_user_session_id=; Path=/; Max-Age=0; SameSite=Lax";
  }

function definit_user_session (pSessionId, pUser)
  {
    if (pSessionId)
      {
        localStorage.setItem (USER_SESSION_KEY, pSessionId);
        Session.set (USER_SESSION_KEY, pSessionId);
        ecrit_cookie_session_diffusion (pSessionId);
      }
    gCurrentUser.set (pUser || null);
    Session.set ("diffusion_connecte", !!pUser);
  }

function email_principal_meteor_user_client (pUser)
  {
    const tEmails = Array.isArray (pUser && pUser.emails) ? pUser.emails : [];
    const tEmail = tEmails.find ((pItem) => pItem && pItem.address);
    return (tEmail && tEmail.address) || '';
  }

function user_public_meteor_client ()
  {
    const tUser = Meteor.user ();
    if (!tUser) return null;
    return {
      _id : tUser._id,
      email : email_principal_meteor_user_client (tUser),
      username : tUser.username || null,
      slug : tUser.slug || null,
      profile : tUser.profile || {},
      roles : Array.isArray (tUser.roles) ? tUser.roles : [],
    };
  }

function email_utilisateur_public (pUser)
  {
    return (pUser && pUser.email)
      || (pUser && pUser.profile && pUser.profile.email)
      || (pUser && pUser.username)
      || '';
  }

function utilisateur_mot_de_passe_actif (pUser)
  {
    return !!(pUser && pUser.auth && pUser.auth.password_enabled);
  }

function mot_de_passe_compte_valide (pPassword)
  {
    return String (pPassword || '').length >= 8;
  }

function message_erreur_compte (pErr, pDefaut)
  {
    if (!pErr) return pDefaut;
    return message_erreur_diffusion (pErr, pDefaut);
  }

function message_erreur_diffusion (pErr, pDefaut)
  {
    const tMessage = String ((pErr && (pErr.reason || pErr.message)) || '').trim ();
    if (!tMessage) return pDefaut;
    const tCle = tMessage.toLowerCase ().replace (/[.!?\s]+$/g, '');
    const tTraductions = {
      'something went wrong. please check your credentials' : 'Connexion impossible. Vérifiez vos identifiants.',
      'incorrect password' : 'Mot de passe incorrect.',
      'user not found' : 'Compte introuvable.',
      'email not found' : 'Adresse e-mail introuvable.',
      'token expired' : 'Lien invalide ou expiré.',
      'token has expired' : 'Lien invalide ou expiré.',
      'invalid token' : 'Lien invalide ou expiré.',
      'invalid login token' : 'Lien de connexion invalide ou expiré.',
      'login token has expired' : 'Lien de connexion invalide ou expiré.',
      'login token expired' : 'Lien de connexion invalide ou expiré.',
    };
    if (tTraductions[tCle]) return tTraductions[tCle];
    if (/credentials/.test (tCle)) return 'Connexion impossible. Vérifiez vos identifiants.';
    if (/token/.test (tCle) && /expired|invalid|not found|not-found/.test (tCle))
      return 'Lien invalide ou expiré.';
    if (/password/.test (tCle) && /incorrect|wrong|invalid/.test (tCle))
      return 'Mot de passe incorrect.';
    if (/user|email|account/.test (tCle) && /not found|not-found|unknown/.test (tCle))
      return 'Compte introuvable.';
    return tMessage || pDefaut;
  }

function rafraichit_etat_compte_diffusion ()
  {
    if (!Meteor.userId ()) return;
    Meteor.call ('diffusion.auth.accountState', (pErr, pRes) =>
      {
        if (pErr || !pRes || !pRes.user) return;
        gCurrentUser.set (pRes.user);
      });
  }

function deconnecte_compte_diffusion ()
  {
    const tSessionId = user_session_id ();
    Meteor.call ('diffusion.auth.logout', {session_id : tSessionId}, () => {});
    oublie_user_session ();
    FlowRouter.go ('/');
    Meteor.logout (() => {});
  }

function demande_lien_passwordless_meteor (pEmail, pVersionId, pReturnPath, pCallback)
  {
    const tEmail = String (pEmail || '').trim ().toLowerCase ();
    const tVersionId = typeof pVersionId === 'string' ? pVersionId.trim () : '';
    const tReturnPath = chemin_entree_passwordless_diffusion (
      tVersionId,
      chemin_local_diffusion_valide (pReturnPath, '/')
    );
    const tUrlParams = parametres_url_inscription_diffusion ();
    Accounts.requestLoginTokenForUser (
      {
        selector : {email : tEmail},
        userData : Object.keys (tUrlParams).length
          ? {email : tEmail, username : tEmail, profile : {url_params : tUrlParams}}
          : {email : tEmail, username : tEmail},
        options : {
          extra : {
            version_id : tVersionId,
            retour : tReturnPath,
            return_path : tReturnPath,
          },
        },
      },
      (pErr, pRes) =>
        {
          if (!pErr) declenche_lead_pixel_diffusion (tEmail, tVersionId);
          if (pCallback) pCallback (pErr, pRes);
        }
    );
  }

function assure_session_cogibulle_apres_login (pOptions = {})
  {
    if (!Meteor.userId ()) return;
    const tVersionId = pOptions.version_id || version_id_route () || gPasswordlessVersionId || '';
    Meteor.call (
      'diffusion.auth.ensureUserSession',
      {
        session_id : user_session_id (),
        version_id : tVersionId,
        page_url : window.location.href,
        return_path : pOptions.return_path || gPasswordlessReturnPath || chemin_entree_passwordless_diffusion (tVersionId, ''),
      },
      (pErr, pRes) =>
        {
          if (pErr || !pRes || !pRes.session_id)
            {
              gCurrentUser.set (user_public_meteor_client ());
              Session.set ("diffusion_connecte", !!Meteor.userId ());
              gAuthReady.set (true);
              demande_etat_acces_diffusion ();
              return;
            }
          definit_user_session (pRes.session_id, pRes.user || user_public_meteor_client ());
          if (pRes.is_new_project_user)
            evenement_pixel_meta ('CompleteRegistration', {}, {event_id : pRes.pixel_registration_event_id || null});
          if (pRes.version_id && pRes.play_session_id)
            definit_session_version (pRes.version_id, pRes.play_session_id);
          const tSuccessMessage = pOptions.success_message || gAuthSuccessMessage;
          if (!tSuccessMessage && pRes.is_new_project_user && pRes.welcome_html)
            affiche_overlay_bienvenue_diffusion (pRes.welcome_html);
          gAuthReady.set (true);
          demande_etat_acces_diffusion ();
          // Ne naviguer que si un chemin de retour explicite existe (lien
          // e-mail, reinitialisation). Sans cela, le secours /jouer/<version_id>
          // ejectait l'utilisateur connecte des URLs a slug public.
          const tRetour = pOptions.return_path || gPasswordlessReturnPath || '';
          gAuthSuccessMessage = null;
          gPasswordlessReturnPath = null;
          gPasswordlessVersionId = null;
          if (tSuccessMessage)
            ferme_interfaces_auth_diffusion_immediatement ();
          if (tRetour)
            FlowRouter.go (chemin_local_diffusion_valide (tRetour, '/'));
          if (tSuccessMessage)
            Meteor.defer (() => affiche_notification_diffusion (tSuccessMessage, 'succes', {duration_ms : 2600}));
        }
    );
  }

function connecte_passwordless_depuis_lien (pSelector, pToken, pVersionId)
  {
    const tSelector = typeof pSelector === 'string' ? pSelector.trim () : '';
    const tToken = typeof pToken === 'string' ? pToken.trim () : '';
    const tVersionId = typeof pVersionId === 'string' ? pVersionId.trim () : '';
    if (!tSelector || !tToken) return;
    const tKey = tSelector + ':' + tToken;
    if (gPasswordlessLoginKey === tKey) return;
    gPasswordlessLoginKey = tKey;
    gPasswordlessVersionId = tVersionId || gPasswordlessVersionId;
    // Ne pas ecraser un chemin de retour deja fourni par le lien e-mail
    // (la route /auth/passwordless y place l'alias public quand il existe) ;
    // /jouer/<version_id> reste le secours.
    gPasswordlessReturnPath = gPasswordlessReturnPath
      || chemin_entree_passwordless_diffusion (gPasswordlessVersionId, '/');
    Meteor.passwordlessLoginWithToken (tSelector, tToken, (pErr) =>
      {
        if (pErr)
          {
            if (Meteor.userId ())
              {
                gAuthSuccessMessage = 'Connexion réussie';
                assure_session_cogibulle_apres_login (
                  {
                    version_id : gPasswordlessVersionId || '',
                    success_message : 'Connexion réussie',
                  }
                );
                return;
              }
            gPasswordlessLoginKey = null;
            ouvre_identification_diffusion (null, {title : TITRE_SESSION_CONNEXION_EXPIREE});
            return;
          }
        assure_session_cogibulle_apres_login (
          {
            version_id : gPasswordlessVersionId || '',
          }
        );
      });
  }

function oublie_user_session ()
  {
    localStorage.removeItem (USER_SESSION_KEY);
    Session.set (USER_SESSION_KEY, null);
    efface_cookie_session_diffusion ();
    gCurrentUser.set (null);
    Session.set ("diffusion_connecte", false);
  }

function session_id_version (pVersionId)
  {
    if (!pVersionId) return null;
    const tCle = cle_session_locale (pVersionId);
    return Session.get (tCle) || localStorage.getItem (tCle);
  }

function definit_session_version (pVersionId, pSessionId)
  {
    if (!pVersionId || !pSessionId) return;
    const tCle = cle_session_locale (pVersionId);
    const tNouvelleSession = localStorage.getItem (tCle) !== pSessionId;
    localStorage.setItem (tCle, pSessionId);
    Session.set (tCle, pSessionId);
    if (tNouvelleSession)
      {
        const tProjet = projet_courant ();
        evenement_pixel_meta ('ViewContent', {
          content_name : (tProjet && tProjet.nom) || 'partie',
          content_ids : [pVersionId],
        });
      }
  }

function scenes_version_courante ()
  {
    const tVersionId = version_id_route ();
    if (!tVersionId) return [];
    return Scenes.find (
      {_diffusion_version_id : tVersionId},
      {sort : {ordre : 1, titre : 1}}
    ).fetch ();
  }

function scene_slug_active ()
  {
    const tScenes = scenes_version_courante ();
    if (!tScenes.length) return null;
    const tSlug = Session.get ('scene_slug');
    if (tSlug && tScenes.some ((pScene) => pScene.slug === tSlug)) return tSlug;
    return (tScenes[0] && tScenes[0].slug)
      || null;
  }

function remplace_url_scene_lecteur_si_necessaire (pVersionId, pSlug)
  {
    const tVersionId = typeof pVersionId === 'string' ? pVersionId.trim () : '';
    const tSlug = typeof pSlug === 'string' ? pSlug.trim () : '';
    if (!tVersionId || !tSlug) return;
    const tRoute = FlowRouter.current && FlowRouter.current ();
    const tParams = tRoute && tRoute.params || {};
    const tUrlVersion = typeof tParams.pVersionId === 'string' ? decodeURIComponent (tParams.pVersionId) : '';
    const tUrlPublicSlug = normalise_slug_public_diffusion (
      typeof tParams.pPublicSlug === 'string' ? decodeURIComponent (tParams.pPublicSlug) : ''
    );
    const tUrlSlug = typeof tParams.pSceneSlug === 'string' ? decodeURIComponent (tParams.pSceneSlug) : '';
    const tSlugPublic = slug_public_route_pour_version (tVersionId);
    if (tSlugPublic && tUrlPublicSlug === tSlugPublic)
      {
        if (tUrlSlug === tSlug) return;
        FlowRouter.go (chemin_scene_lecteur_diffusion (tVersionId, tSlug));
        return;
      }
    if (tUrlVersion !== tVersionId) return;
    if (tUrlSlug === tSlug) return;
    FlowRouter.go (chemin_scene_lecteur_diffusion (tVersionId, tSlug));
  }

// Les scènes marquées _diffusion_scene_legere par le serveur (complément de
// la publication, sans objets_config ni svg) servent aux listes et à la
// résolution des slugs, jamais au rendu : on attend le document complet,
// que le réabonnement avec le nouveau slug apporte.
function scene_complete_client (pVersionId, pSlug)
  {
    const tSelecteur = {_diffusion_version_id : pVersionId, _diffusion_scene_legere : {$ne : true}};
    return (pSlug && Scenes.findOne ({...tSelecteur, slug : pSlug}))
      || Scenes.findOne (tSelecteur, {sort : {ordre : 1, titre : 1}})
      || null;
  }

function scene_active ()
  {
    const tVersionId = version_id_route ();
    const tSlug = scene_slug_active ();
    if (!tVersionId) return null;
    return scene_complete_client (tVersionId, tSlug);
  }

function scenes_manifest_courant ()
  {
    return scenes_version_courante ();
  }

function scene_par_id_ou_slug (pId)
  {
    const tId = typeof pId === 'string' ? pId : '';
    if (!tId) return null;
    return scenes_manifest_courant ().find ((pScene) =>
      pScene._id === tId || pScene.id === tId || pScene.slug === tId
    ) || null;
  }

function scene_access_par_id_ou_slug (pId)
  {
    const tId = typeof pId === 'string' ? pId.trim () : '';
    if (!tId) return null;
    return DiffusionSceneAccess.findOne (
      {
        $or : [
          {scene_id : tId},
          {slug : tId},
        ],
      }
    );
  }

function scene_cible_verrouillee_diffusion (pId)
  {
    const tId = typeof pId === 'string' ? pId.trim () : '';
    if (!tId) return false;
    // Mode embarqué : toutes les scènes sont hydratées localement ; le
    // verrou dépend du switch « Accès libre » et de l'achat in-app.
    if (est_runtime_embarque ()) return scene_verrouillee_embarque (tId);
    if (scene_par_id_ou_slug (tId)) return false;
    const tAccess = scene_access_par_id_ou_slug (tId);
    return !!(tAccess && tAccess.acces_libre !== true);
  }

function normalise_vue (pVue)
  {
    const tVue = pVue && typeof pVue === 'object' ? pVue : null;
    if (!tVue) return null;
    const tX = Number (tVue.x);
    const tY = Number (tVue.y);
    const tW = Number (tVue.w);
    const tH = Number (tVue.h);
    if (![tX, tY, tW, tH].every (Number.isFinite) || tW <= 0 || tH <= 0) return null;
    return {x : tX, y : tY, w : tW, h : tH};
  }

function parse_viewbox (pValeur)
  {
    const tParts = String (pValeur || '').trim ().split (/[\s,]+/).map (Number);
    if (tParts.length < 4 || !tParts.every (Number.isFinite)) return null;
    return normalise_vue ({x : tParts[0], y : tParts[1], w : tParts[2], h : tParts[3]});
  }

function viewbox_attr (pVue)
  {
    const tVue = normalise_vue (pVue);
    return tVue ? `${tVue.x} ${tVue.y} ${tVue.w} ${tVue.h}` : '';
  }

function ratio_manifest_courant ()
  {
    const tProjet = projet_courant ();
    const tRapport = tProjet && tProjet.rapport_lh_scene;
    const tLargeur = Number (tRapport && tRapport.largeur);
    const tHauteur = Number (tRapport && tRapport.hauteur);
    if (Number.isFinite (tLargeur) && Number.isFinite (tHauteur) && tLargeur > 0 && tHauteur > 0)
      return `${tLargeur} / ${tHauteur}`;
    return '16 / 9';
  }

function ratio_manifest_courant_num ()
  {
    const tProjet = projet_courant ();
    const tRapport = tProjet && tProjet.rapport_lh_scene;
    const tLargeur = Number (tRapport && tRapport.largeur);
    const tHauteur = Number (tRapport && tRapport.hauteur);
    if (Number.isFinite (tLargeur) && Number.isFinite (tHauteur) && tLargeur > 0 && tHauteur > 0)
      return tLargeur / tHauteur;
    return 16 / 9;
  }

function lecture_settings ()
  {
    const tProjet = projet_courant ();
    return (tProjet && tProjet.reglages_mode_lecture) || {};
  }

function lecture_bubble_settings ()
  {
    const tProjet = projet_courant ();
    return (tProjet && tProjet.reglages_bulles) || {};
  }

function audio_entry_url (pEntree)
  {
    if (!pEntree) return null;
    const tUrl = typeof pEntree === 'string'
      ? pEntree
      : (pEntree.url || pEntree.audio_url || null);
    return url_asset_diffusion_protegee (tUrl, version_id_route ());
  }

function project_audio (pCle)
  {
    const tProjet = projet_courant ();
    const tAudio = tProjet && tProjet.audio_assets;
    return tAudio && tAudio[pCle];
  }

function project_ambiance_entries ()
  {
    const tProjet = projet_courant ();
    const tListe = tProjet && tProjet.musique_ambiance;
    return Array.isArray (tListe) ? tListe : [];
  }

function prepare_audio_url_tpl (pTpl, pUrl)
  {
    if (!pTpl || !pUrl) return null;
    if (!pTpl._lectureAudioPlayer) pTpl._lectureAudioPlayer = create_lecture_audio_player ();
    return pTpl._lectureAudioPlayer.prepare (pUrl);
  }

function prepare_audio_entry_tpl (pTpl, pEntree)
  {
    const tUrl = audio_entry_url (pEntree);
    if (tUrl) prepare_audio_url_tpl (pTpl, tUrl);
  }

function prepare_audios_lecture_tpl (pTpl, pProjet, pScene)
  {
    if (!pTpl || !pProjet) return;
    [
      'son_clic',
      'son_appareil_photo',
      'son_retour_menu',
      'son_reussite',
      'son_echec',
      'entends_tu_le_son_1',
      'entends_tu_le_son_2',
      'musique_animation_liane',
      'musique_pluie_de_bananes',
    ].forEach ((pCle) => prepare_audio_entry_tpl (pTpl, pProjet.audio_assets && pProjet.audio_assets[pCle]));

    (Array.isArray (pProjet.musique_ambiance) ? pProjet.musique_ambiance : [])
      .forEach ((pEntree) => prepare_audio_entry_tpl (pTpl, pEntree));

    Object.values ((pScene && pScene.audio_assets) || {})
      .forEach ((pEntree) => prepare_audio_entry_tpl (pTpl, pEntree));

    objets_config_scene (pScene).forEach ((pConfig) =>
      {
        if (pConfig && pConfig.audio_url)
          {
            prepare_audio_url_tpl (pTpl, pConfig.audio_url);
            prepare_audio_url_tpl (pTpl, audio_entry_url ({url : pConfig.audio_url}));
          }
      });
  }

function hype_widget (pType)
  {
    const tVersionId = version_id_route ();
    const tWidget = tVersionId
      ? HypeWidgets.findOne ({_diffusion_version_id : tVersionId, type : pType})
      : null;
    return tWidget
      ? {...tWidget, url : url_asset_diffusion_protegee (tWidget.url, version_id_route ())}
      : null;
  }

function scene_audio (pScene, pCle)
  {
    const tAudio = pScene && pScene.audio_assets;
    return tAudio && tAudio[pCle];
  }

function volume_audio (pEntree)
  {
    const tVolume = Number (pEntree && pEntree.volume_pct);
    if (!Number.isFinite (tVolume)) return 1;
    return Math.max (0, Math.min (1, tVolume / 100));
  }

function volume_lecture_setting (pCle)
  {
    const tVolume = Number (lecture_settings ()[pCle]);
    if (!Number.isFinite (tVolume)) return 1;
    return Math.max (0, Math.min (1, tVolume / 100));
  }

function volume_audio_html (pEntree)
  {
    return volume_audio (pEntree) * volume_lecture_setting ('audio_general_volume_pct');
  }

function volume_audio_webaudio (pEntree)
  {
    return volume_audio (pEntree) * volume_lecture_setting ('audio_general_volume_pct');
  }

function libelle_objet (pId)
  {
    return String (pId || '')
      .replace (/^(obj|prop|zoog|bouton)_/i, '')
      .replace (/_?son[-_](oui|non)$/i, '')
      .replace (/_OFF$/i, '')
      .replace (/_ON$/i, '')
      .replace (/[_-]+/g, ' ')
      .trim ();
  }

function segments_nom_svg (pValeur)
  {
    return String (pValeur || '')
      .split (',')
      .map ((pSegment) => pSegment.trim ())
      .filter (Boolean);
  }

function nom_config_objet_lecture (pConfig)
  {
    if (!pConfig) return '';
    const tNom = pConfig.item_nom || pConfig.nom || pConfig.titre || pConfig.label || '';
    if (tNom) return String (tNom);
    const tDataName = pConfig['data-name'] || pConfig.data_name || pConfig.name || '';
    const tSegments = segments_nom_svg (tDataName);
    return tSegments[1] || tSegments[0] || '';
  }

function objets_config_scene (pScene = scene_active ())
  {
    const tListe = pScene && Array.isArray (pScene.objets_config) ? pScene.objets_config : [];
    return tListe.filter ((pObjet) => pObjet && pObjet.id);
  }

function objet_config_scene (pId, pScene = scene_active ())
  {
    const tId = typeof pId === 'string' ? pId : '';
    return objets_config_scene (pScene).find ((pObjet) => pObjet.id === tId) || null;
  }

function data_name_svg_scene_pour_id (pId, pScene = scene_active ())
  {
    const tObjets = pScene && pScene.svg_objets && typeof pScene.svg_objets === 'object'
      ? pScene.svg_objets
      : {};
    for (const tListe of Object.values (tObjets))
      {
        if (!Array.isArray (tListe)) continue;
        const tEntree = tListe.find ((pObjet) => pObjet && pObjet.id === pId);
        if (tEntree) return tEntree['data-name'] || tEntree.data_name || '';
      }
    return '';
  }

function nom_objet_lecture (pId, pScene = scene_active ())
  {
    const tConfig = objet_config_scene (pId, pScene);
    const tNomConfig = nom_config_objet_lecture (tConfig);
    if (tNomConfig) return tNomConfig;
    const tSegments = segments_nom_svg (data_name_svg_scene_pour_id (pId, pScene));
    return tSegments[1] || tSegments[0] || libelle_objet (pId);
  }

function bouton_retour_lecture_scene (pScene = scene_active ())
  {
    return objets_config_scene (pScene).find ((pConfig) =>
      pConfig
      && pConfig.id
      && String (nom_objet_lecture (pConfig.id, pScene)).trim ().toLowerCase ().includes ('retour')
      && (
        pConfig.interactivite_action
        || pConfig.interactivite_scene_id
        || pConfig.interactivite_url
        || pConfig.audio_url
      )
    ) || null;
  }

function ids_svg_scene (pScene = scene_active ())
  {
    const tObjets = pScene && pScene.svg_objets && typeof pScene.svg_objets === 'object'
      ? pScene.svg_objets
      : {};
    const tIds = new Set ();
    for (const tListe of Object.values (tObjets))
      if (Array.isArray (tListe))
        tListe.forEach ((pObjet) =>
          {
            if (pObjet && pObjet.id) tIds.add (pObjet.id);
          });
    objets_config_scene (pScene).forEach ((pObjet) => tIds.add (pObjet.id));
    return tIds;
  }

function carte_scene (pCarteId, pScene = scene_active ())
  {
    if (!pScene || !pCarteId) return null;
    return Cartes.findOne ({_id : pCarteId, scene_id : pScene._id})
      || Cartes.findOne ({id : pCarteId, scene_id : pScene._id})
      || null;
  }

function cartes_scene (pScene = scene_active ())
  {
    return pScene
      ? Cartes.find ({scene_id : pScene._id}, {sort : {ordre : 1, nom : 1}}).fetch ()
      : [];
  }

function carte_objets_oui (pCarte)
  {
    const tIds = Array.isArray (pCarte && pCarte.ordre_objets) ? pCarte.ordre_objets : [];
    return tIds.filter ((pId) => /son[-_]oui/i.test (String (pId || '')));
  }

function id_carte_lecture (pCarte)
  {
    return pCarte && (pCarte.id || pCarte._id) || '';
  }

function url_svg_bulle_carte (pCarte)
  {
    if (!pCarte) return null;
    if (pCarte.svg_bulle_url)
      return url_asset_diffusion_protegee (pCarte.svg_bulle_url, version_id_route ());
    const tFichier = pCarte.svg_bulle_id ? HubFiles.findOne ({_id : pCarte.svg_bulle_id}) : null;
    return typeof HubFiles.link === 'function' ? HubFiles.link (tFichier) : null;
  }

function url_ombre_bulle_carte (pCarte)
  {
    const tId = typeof (pCarte && pCarte.bulle_ombre_asset_file_id) === 'string'
      ? pCarte.bulle_ombre_asset_file_id.trim ()
      : '';
    if (!tId) return null;
    const tNom = typeof pCarte.bulle_ombre_asset_file_name === 'string' && pCarte.bulle_ombre_asset_file_name.trim ()
      ? pCarte.bulle_ombre_asset_file_name.trim ()
      : `${tId}.png`;
    return `/api/scene-shadow-assets/${encodeURIComponent (tId)}/${encodeURIComponent (tNom)}`;
  }

function position_bulle_carte (pCarte)
  {
    const tPosition = normalise_vue (pCarte && pCarte.position_bulle);
    if (tPosition) return {x : tPosition.x, y : tPosition.y};
    const tPos = pCarte && pCarte.position_bulle;
    if (tPos && Number.isFinite (Number (tPos.x)) && Number.isFinite (Number (tPos.y)))
      return {x : Number (tPos.x), y : Number (tPos.y)};
    const tVue = normalise_vue (pCarte && pCarte.vue);
    if (tVue) return {x : tVue.x + tVue.w / 2, y : tVue.y + tVue.h / 2};
    return null;
  }

function couleur_bulle_carte (pCarte)
  {
    const tCouleur = typeof (pCarte && pCarte.couleur_bulle) === 'string' ? pCarte.couleur_bulle : '';
    if (tCouleur) return tCouleur;
    const tReglages = lecture_bubble_settings ();
    const tCouleurs = Array.isArray (tReglages.couleurs_auto) ? tReglages.couleurs_auto : [];
    if (!tCouleurs.length) return '#ff8647';
    const tSeed = String (id_carte_lecture (pCarte)).split ('')
      .reduce ((pTotal, pChar) => pTotal + pChar.charCodeAt (0), 0);
    return tCouleurs[tSeed % tCouleurs.length] || '#ff8647';
  }

function taille_bulle_carte (pCarte)
  {
    const tTaille = Number (pCarte && pCarte.taille_bulle);
    if (Number.isFinite (tTaille) && tTaille > 0) return tTaille;
    const tReglages = lecture_bubble_settings ();
    const tDefaut = Number (tReglages.taille_bulle_custom_defaut);
    return Number.isFinite (tDefaut) && tDefaut > 0 ? tDefaut : 86;
  }

function ratio_hash_lecture (pValeur)
  {
    const tTexte = String (pValeur || '');
    let tHash = 2166136261;
    for (let i = 0; i < tTexte.length; i++)
      {
        tHash ^= tTexte.charCodeAt (i);
        tHash = Math.imul (tHash, 16777619);
      }
    return (tHash >>> 0) / 4294967295;
  }

function hype_prefix_type (pType)
  {
    return pType === 'recompense_scene' ? 'hype_recompense_scene' : 'hype_recompense_carte';
  }

function hype_options_type (pType)
  {
    const tReglages = lecture_settings ();
    const tPrefix = hype_prefix_type (pType);
    return {
      fond_transparent: tReglages[`${tPrefix}_fond_transparent`] !== false,
      couleur_fond    : tReglages[`${tPrefix}_couleur_fond`] || '#ffffff',
      taille_pct      : Math.max (10, Math.min (100, Number (tReglages[`${tPrefix}_taille_pct`]) || 100)),
      align_x         : tReglages[`${tPrefix}_align_x`] || 'centre',
      align_y         : tReglages[`${tPrefix}_align_y`] || 'centre',
      offset_x_pct    : Number (tReglages[`${tPrefix}_offset_x_pct`]) || 0,
      offset_y_pct    : Number (tReglages[`${tPrefix}_offset_y_pct`]) || 0,
      disparition_ms  : Math.max (300, Number (tReglages[`${tPrefix}_disparition_ms`]) || 2400),
      fadeout_ms      : Math.max (80, Number (tReglages[`${tPrefix}_fadeout_ms`]) || 450),
    };
  }

function date_label (pDate)
  {
    if (!pDate) return 'Jamais';
    const tDate = pDate instanceof Date ? pDate : new Date (pDate);
    if (Number.isNaN (tDate.getTime ())) return 'Jamais';
    return tDate.toLocaleString ('fr-FR',
      {
        dateStyle: 'short',
        timeStyle: 'short',
      });
  }

function access_label (pMode)
  {
    if (pMode === 'password') return 'Mot de passe';
    if (pMode === 'passwordless') return 'Passwordless';
    return 'Public';
  }

function demande_etat_acces_diffusion ()
  {
    // Mode embarqué : aucun serveur à interroger (DDP coupé, l'appel resterait
    // en file d'attente indéfiniment) ; l'accès est réglé par l'achat in-app.
    if (est_runtime_embarque ())
      {
        gAccessState.set ({authorized : false, limited : false});
        Session.set ("diffusion_connecte", false);
        return;
      }
    const tVersionId = version_id_route ();
    if (!tVersionId)
      {
        gAccessState.set ({authorized : false, limited : false});
        Session.set ("diffusion_connecte", false);
        return;
      }
    Meteor.call (
      'diffusion.project.accessState',
      {version_id : tVersionId, user_session_id : user_session_id ()},
      (pErr, pRes) =>
        {
          if (pErr || !pRes)
            {
              gAccessState.set ({authorized : false, limited : false});
              Session.set ("diffusion_connecte", false);
              return;
            }
          gAccessState.set (pRes);
          if (pRes.user) gCurrentUser.set (pRes.user);
          Session.set ("diffusion_connecte", !!pRes.user);
        }
    );
  }

function ouvre_identification_diffusion (pMessage = null, pOptions = {})
  {
    ferme_carte_active_diffusion ();
    if (gAccessGateCloseTimer)
      {
        clearTimeout (gAccessGateCloseTimer);
        gAccessGateCloseTimer = null;
      }
    gAccessGateClosing.set (false);
    gAccessGateMessage.set (pMessage);
    gAccessGateTitle.set (pOptions.title || null);
    gAccessGateForm.set (true);
    gAccessGateRendered.set (true);
    gAccessGateOpen.set (true);
  }

function message_passwordless_envoye (pRes)
  {
    const tDelivery = pRes && pRes.delivery;
    if (tDelivery && tDelivery.mode === 'local-dev' && tDelivery.confirmation_link)
      return {
        type : 'succes',
        texte : 'SMTP non configuré sur diffusion. Lien de confirmation local : ' + tDelivery.confirmation_link,
      };
    return {type : 'succes', texte : 'E-mail envoyé.'};
  }

function gere_resultat_envoi_passwordless_identification (pRes)
  {
    const tMessage = message_passwordless_envoye (pRes);
    const tDelivery = pRes && pRes.delivery;
    if (tDelivery && tDelivery.mode === 'local-dev' && tDelivery.confirmation_link)
      {
        gAccessGateMessage.set (tMessage);
        return;
      }
    ferme_identification_diffusion ();
    affiche_dialogue_passwordless_envoye ();
  }

function ferme_identification_diffusion ()
  {
    if (gAccessGateBusy.get ()) return;
    if (!gAccessGateRendered.get () || gAccessGateClosing.get ()) return;
    gAccessGateOpen.set (false);
    gAccessGateClosing.set (true);
    gAccessGateCloseTimer = setTimeout (() =>
      {
        gAccessGateCloseTimer = null;
        gAccessGateRendered.set (false);
        gAccessGateClosing.set (false);
        gAccessGateForm.set (false);
        gAccessGateMessage.set (null);
        gAccessGateTitle.set (null);
      }, 420);
  }

window.cogibulleDiffusionAccessGate = function ()
  {
    const tContexte = arguments.length ? arguments[0] : null;
    if (tContexte && tContexte.action === 'account') return true;
    // Mode embarqué : une cible verrouillée ouvre la gate d'achat in-app
    // (gate parentale puis achat/restauration), jamais l'identification.
    if (est_runtime_embarque ())
      {
        const tSceneId = tContexte && tContexte.scene_id;
        if (tSceneId && scene_verrouillee_embarque (tSceneId))
          {
            ouvre_achat_embarque ();
            return false;
          }
        return true;
      }
    const tEtat = gAccessState.get ();
    if (!tEtat || !tEtat.limited) return true;
    ouvre_identification_diffusion ();
    return false;
  };

function titre_fenetre_diffusion_sans_prefixe_test (pTitre)
  {
    const tTitre = String (pTitre || '').trim ();
    return tTitre.replace (/^(?:TEST\b\s*[-–—:]\s*)+/i, '').trim () || tTitre;
  }

function nom_serveur_diffusion_courant ()
  {
    const tNode = DiffusionNodeState.findOne (DIFFUSION_NODE_ID);
    return String ((tNode && tNode.label) || '').trim () || 'diffusion';
  }

function titre_document_diffusion ()
  {
    const tCommunication = communication_diffusion_courante ();
    const tShareTitle = tCommunication && tCommunication.share && typeof tCommunication.share.title === 'string'
      ? tCommunication.share.title.trim ()
      : '';
    if (tShareTitle) return titre_fenetre_diffusion_sans_prefixe_test (tShareTitle);

    let tTitre = nom_serveur_diffusion_courant ();
    if (Session.get ('route') === 'lecteur')
      {
        const tProjet = Projets.findOne (Session.get ('projet_id'));
        const tScene = Scenes.findOne (Session.get ('scene_id'));
        const tProjetNom = titre_fenetre_diffusion_sans_prefixe_test (tProjet && (tProjet.nom || tProjet.titre));
        const tSceneNom = titre_fenetre_diffusion_sans_prefixe_test (tScene && (tScene.titre || tScene.nom));
        if (tProjetNom && tSceneNom) tTitre = `${tProjetNom} - ${tSceneNom}`;
        else if (tProjetNom) tTitre = tProjetNom;
      }
    return titre_fenetre_diffusion_sans_prefixe_test (tTitre);
  }

function texte_partage_diffusion ()
  {
    const tCommunication = communication_diffusion_courante ();
    const tShareText = tCommunication && tCommunication.share && typeof tCommunication.share.text === 'string'
      ? tCommunication.share.text.trim ()
      : '';
    if (tShareText) return tShareText;
    const tProjet = Projets.findOne (Session.get ('projet_id')) || projet_courant ();
    return typeof (tProjet && tProjet.description) === 'string' ? tProjet.description.trim () : '';
  }

function url_absolue_diffusion (pUrl)
  {
    const tUrl = typeof pUrl === 'string' ? pUrl.trim () : '';
    if (!tUrl) return '';
    try { return new URL (tUrl, window.location.origin).href; }
    catch (e) { return tUrl; }
  }

function pose_meta_diffusion (pSelecteur, pAttribut, pValeur)
  {
    if (typeof document === 'undefined') return;
    const tValeur = typeof pValeur === 'string' ? pValeur.trim () : '';
    let tMeta = document.head && document.head.querySelector (pSelecteur);
    if (!tValeur)
      {
        if (tMeta && tMeta.parentNode) tMeta.parentNode.removeChild (tMeta);
        return;
      }
    if (!tMeta)
      {
        tMeta = document.createElement ('meta');
        if (pSelecteur.startsWith ('meta[property="'))
          tMeta.setAttribute ('property', pSelecteur.slice (15, -2));
        else if (pSelecteur.startsWith ('meta[name="'))
          tMeta.setAttribute ('name', pSelecteur.slice (11, -2));
        document.head.appendChild (tMeta);
      }
    tMeta.setAttribute (pAttribut, tValeur);
  }

function applique_meta_partage_diffusion ()
  {
    if (typeof document === 'undefined') return;
    const tTitre = titre_fenetre_diffusion_sans_prefixe_test (titre_document_diffusion ());
    const tTexte = texte_partage_diffusion ();
    const tImage = url_absolue_diffusion (image_partage_communication_diffusion_url ());
    const tUrl = url_absolue_diffusion (window.location && window.location.href);

    document.title = tTitre;
    pose_meta_diffusion ('meta[name="description"]', 'content', tTexte);
    pose_meta_diffusion ('meta[property="og:type"]', 'content', 'website');
    pose_meta_diffusion ('meta[property="og:title"]', 'content', tTitre);
    pose_meta_diffusion ('meta[property="og:description"]', 'content', tTexte);
    pose_meta_diffusion ('meta[property="og:image"]', 'content', tImage);
    pose_meta_diffusion ('meta[property="og:url"]', 'content', tUrl);
    pose_meta_diffusion ('meta[name="twitter:card"]', 'content', tImage ? 'summary_large_image' : 'summary');
    pose_meta_diffusion ('meta[name="twitter:title"]', 'content', tTitre);
    pose_meta_diffusion ('meta[name="twitter:description"]', 'content', tTexte);
    pose_meta_diffusion ('meta[name="twitter:image"]', 'content', tImage);
    applique_liens_webapp_diffusion ();
  }

// Webapp (installation sur l'écran d'accueil) : le HTML initial porte déjà le manifeste et les icônes du
// jeu demandé, posés par le serveur. En navigation interne (catalogue → jeu, ou retour), la page n'est
// pas rechargée : on les pose ou on les retire ici, pour que l'installation proposée soit toujours celle
// du jeu affiché. Sans objet dans l'appli embarquée.
function applique_liens_webapp_diffusion ()
  {
    if (typeof document === 'undefined' || est_runtime_embarque ()) return;
    const tSegments = String (window.location.pathname || '').split ('/').filter (Boolean);
    const tVersionId = Session.get ('route') === 'lecteur' ? Session.get ('version_id') : null;
    let tDepart = '';
    if (tVersionId && tSegments.length)
      tDepart = tSegments[0] === 'jouer'
        ? (tSegments[1] ? `/jouer/${tSegments[1]}` : '')
        : `/${tSegments[0]}`;
    const pose_lien = (pRel, pHref, pAttributs = {}) =>
      {
        let tLien = document.head.querySelector (`link[rel="${pRel}"]`);
        if (!pHref)
          {
            if (tLien) tLien.remove ();
            return;
          }
        if (!tLien)
          {
            tLien = document.createElement ('link');
            tLien.setAttribute ('rel', pRel);
            document.head.appendChild (tLien);
          }
        Object.keys (pAttributs).forEach ((pCle) => tLien.setAttribute (pCle, pAttributs[pCle]));
        if (tLien.getAttribute ('href') !== pHref) tLien.setAttribute ('href', pHref);
      };
    const tIcone = (pTaille) => tDepart ? `/webapp/icone/${encodeURIComponent (tVersionId)}/${pTaille}.png` : '';
    // Variante (canal de test, lecteur Pixi, projet) conservée dans le manifeste : la webapp
    // installée depuis une page « Tester 2 » rouvre le même lecteur (miroir du serveur).
    const tVariante = (() =>
      {
        try
          {
            const tSource = new URLSearchParams (window.location.search || '');
            const tParams = new URLSearchParams ();
            ['cogi_test', 'lecteur', 'projet'].forEach ((pCle) => { const tV = tSource.get (pCle); if (tV && tV.trim ()) tParams.set (pCle, tV.trim ()); });
            return tParams.toString ();
          }
        catch (e) { return ''; }
      }) ();
    pose_lien ('manifest', tDepart
      ? `/webapp/manifeste.webmanifest?chemin=${encodeURIComponent (tDepart)}` + (tVariante ? `&variante=${encodeURIComponent (tVariante)}` : '')
      : '');
    pose_lien ('apple-touch-icon', tIcone (180));
    pose_lien ('icon', tIcone (192), {type : 'image/png', sizes : '192x192'});
    if (tDepart)
      {
        pose_meta_diffusion ('meta[name="mobile-web-app-capable"]', 'content', 'yes');
        pose_meta_diffusion ('meta[name="apple-mobile-web-app-capable"]', 'content', 'yes');
        pose_meta_diffusion ('meta[name="apple-mobile-web-app-status-bar-style"]', 'content', 'black-translucent');
      }
    // Nom court : iOS ne lit pas short_name du manifeste, mais apple-mobile-web-app-title, et à défaut le
    // titre de la page (celui de partage). Même repli que le serveur : réglage webapp, sinon nom du projet.
    const tProjet = tDepart ? Projets.findOne ({_diffusion_version_id : tVersionId}) : null;
    const tNomCourt = tProjet
      ? String ((tProjet.webapp && tProjet.webapp.nom) || tProjet.nom || '').trim ()
      : '';
    pose_meta_diffusion ('meta[name="apple-mobile-web-app-title"]', 'content', tNomCourt);
    pose_meta_diffusion ('meta[name="application-name"]', 'content', tNomCourt);
  }

function demarre_session_publique (pTpl, pVersion)
  {
    if (est_runtime_embarque ()) return;
    if (!pVersion || pVersion.access_mode !== 'public') return;
    const tUserSessionId = user_session_id ();
    if (!tUserSessionId) return;
    if (session_id_version (pVersion._id)) return;
    if (pTpl._sessionPubliqueEnCours === pVersion._id) return;

    pTpl._sessionPubliqueEnCours = pVersion._id;
    Meteor.call ('diffusion.access.start', {version_id : pVersion._id, user_session_id : tUserSessionId}, (pErr, pRes) =>
      {
        pTpl._sessionPubliqueEnCours = null;
        if (pErr || !pRes || !pRes.session_id) return;
        definit_session_version (pVersion._id, pRes.session_id);
      });
  }

function signale_evenement_lecture (pType, pCardId = null)
  {
    if (est_runtime_embarque ()) return;
    const tVersionId = version_id_route ();
    const tSessionId = session_id_version (tVersionId);
    if (!tVersionId || !tSessionId) return;
    const tScene = scene_active ();
    Meteor.call ('diffusion.session.event',
      {
        version_id : tVersionId,
        session_id : tSessionId,
        type       : pType,
        scene_id   : tScene && (tScene.id || tScene._id),
        card_id    : pCardId,
      });
  }

function stop_audio_tpl (pTpl)
  {
    if (pTpl && pTpl._lectureAudioPlayer) pTpl._lectureAudioPlayer.stop ();
    if (!pTpl || !pTpl._lectureAudio) return;
    try { pTpl._lectureAudio.pause (); } catch (e) {}
    try { pTpl._lectureAudio.currentTime = 0; } catch (e) {}
    pTpl._lectureAudio = null;
  }

function stop_ambiance_tpl (pTpl)
  {
    if (!pTpl || !pTpl._lectureAmbianceAudio) return;
    try { pTpl._lectureAmbianceAudio.pause (); } catch (e) {}
    try { pTpl._lectureAmbianceAudio.currentTime = 0; } catch (e) {}
    pTpl._lectureAmbianceAudio = null;
  }

function play_audio_url_tpl (pTpl, pUrl, pOptions = {})
  {
    if (!pUrl) return Promise.resolve (false);
    stop_audio_tpl (pTpl);
    if (!pTpl) return Promise.resolve (false);
    if (!pTpl._lectureAudioPlayer) pTpl._lectureAudioPlayer = create_lecture_audio_player ();
    return pTpl._lectureAudioPlayer.play (pUrl, {volume : volume_audio_webaudio (pOptions.entry)});
  }

async function play_audio_sequence_tpl (pTpl, pEntrees)
  {
    const tEntrees = Array.isArray (pEntrees) ? pEntrees.filter (Boolean) : [];
    for (const tEntree of tEntrees)
      {
        const tUrl = audio_entry_url (tEntree);
        if (!tUrl) continue;
        await play_audio_url_tpl (pTpl, tUrl, {entry : tEntree});
      }
  }

function start_ambiance_tpl (pTpl)
  {
    if (!pTpl || pTpl._lectureAmbianceAudio) return;
    const tEntree = project_ambiance_entries ().find ((pItem) => !!audio_entry_url (pItem));
    const tUrl = audio_entry_url (tEntree);
    if (!tUrl) return;
    const tAudio = new Audio (tUrl);
    tAudio.loop = true;
    tAudio.preload = 'auto';
    tAudio.volume = Math.min (0.55, volume_audio_html (tEntree));
    pTpl._lectureAmbianceAudio = tAudio;
    const tPlay = tAudio.play ();
    if (tPlay && typeof tPlay.catch === 'function')
      tPlay.catch (() =>
        {
          if (pTpl._lectureAmbianceAudio === tAudio) pTpl._lectureAmbianceAudio = null;
        });
  }

function clear_hype_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._lectureHypeHideTimer) clearTimeout (pTpl._lectureHypeHideTimer);
    if (pTpl._lectureHypeClearTimer) clearTimeout (pTpl._lectureHypeClearTimer);
    pTpl._lectureHypeHideTimer = null;
    pTpl._lectureHypeClearTimer = null;
    pTpl.hypeOverlay && pTpl.hypeOverlay.set (null);
  }

function show_hype_tpl (pTpl, pType, pOptions = {})
  {
    const tWidget = hype_widget (pType);
    if (!pTpl || !tWidget) return false;
    const tOptions = hype_options_type (pType);
    clear_hype_tpl (pTpl);
    pTpl.hypeOverlay.set (
      {
        type      : pType,
        url       : `${tWidget.url}${tWidget.url.includes ('?') ? '&' : '?'}diffusion=${Date.now ()}`,
        options   : tOptions,
        hiding    : false,
      });
    pTpl._lectureHypeHideTimer = setTimeout (() =>
      {
        const tCourant = pTpl.hypeOverlay.get ();
        if (!tCourant) return;
        pTpl.hypeOverlay.set ({...tCourant, hiding : true});
        pTpl._lectureHypeClearTimer = setTimeout (() =>
          {
            pTpl.hypeOverlay.set (null);
            pTpl._lectureHypeClearTimer = null;
            if (pOptions && typeof pOptions.onDone === 'function')
              pOptions.onDone ();
          }, tOptions.fadeout_ms);
      }, tOptions.disparition_ms);
    return true;
  }

function scene_entends_tu_sequence (pScene, pObjetConfig = null)
  {
    return [
      project_audio ('entends_tu_le_son_1'),
      scene_audio (pScene, 'phoneme'),
      project_audio ('entends_tu_le_son_2'),
      pObjetConfig && {url : pObjetConfig.audio_url, volume_pct : 100},
    ].filter ((pEntree) => !!audio_entry_url (pEntree));
  }

function svg_el (pNom)
  {
    return document.createElementNS (LECTURE_SVG_NS, pNom);
  }

function node_id_depuis_event (pEvent, pRoot, pIds)
  {
    let tNode = pEvent && pEvent.target;
    while (tNode && tNode !== pRoot)
      {
        if (tNode.getAttribute)
          {
            const tCardId = tNode.getAttribute ('data-diffusion-card-id');
            if (tCardId) return {type : 'card', id : tCardId, node : tNode};
            const tId = tNode.getAttribute ('id');
            if (tId && pIds && pIds.has (tId)) return {type : 'object', id : tId, node : tNode};
          }
        tNode = tNode.parentNode;
      }
    return null;
  }

function set_svg_viewbox (pTpl, pVue)
  {
    const tVue = normalise_vue (pVue);
    const tRoot = pTpl && pTpl._lectureSvgRoot;
    if (!tRoot || !tVue) return false;
    tRoot.setAttribute ('viewBox', viewbox_attr (tVue));
    pTpl._lectureViewBox = {...tVue};
    return true;
  }

function anime_viewbox_tpl (pTpl, pCible, pDuree = 700, pOptions = {})
  {
    const tDepart = normalise_vue (pTpl && pTpl._lectureViewBox);
    const tCible = normalise_vue (pCible);
    if (!tDepart || !tCible || !pTpl) return;
    const tDuree = Math.max (0, Number (pDuree) || 0);
    if (pTpl._lectureViewBoxRaf) cancelAnimationFrame (pTpl._lectureViewBoxRaf);
    const tAnimationId = ((pTpl._lectureViewBoxAnimationId || 0) + 1);
    pTpl._lectureViewBoxAnimationId = tAnimationId;
    if (tDuree <= 0)
      {
        set_svg_viewbox (pTpl, tCible);
        if (pOptions && typeof pOptions.onComplete === 'function')
          pOptions.onComplete ();
        return;
      }

    const tDebut = performance.now ();
    const ease = (pT) => pT < 0.5 ? 4 * pT * pT * pT : 1 - Math.pow (-2 * pT + 2, 3) / 2;
    const frame = (pNow) =>
      {
        if (pTpl._lectureViewBoxAnimationId !== tAnimationId) return;
        const t = Math.min (1, (pNow - tDebut) / tDuree);
        const e = ease (t);
        set_svg_viewbox (pTpl,
          {
            x : tDepart.x + (tCible.x - tDepart.x) * e,
            y : tDepart.y + (tCible.y - tDepart.y) * e,
            w : tDepart.w + (tCible.w - tDepart.w) * e,
            h : tDepart.h + (tCible.h - tDepart.h) * e,
          });
        if (t < 1) pTpl._lectureViewBoxRaf = requestAnimationFrame (frame);
        else
          {
            pTpl._lectureViewBoxRaf = null;
            if (pTpl._lectureViewBoxAnimationId !== tAnimationId) return;
            if (pOptions && typeof pOptions.onComplete === 'function')
              pOptions.onComplete ();
          }
      };
    pTpl._lectureViewBoxRaf = requestAnimationFrame (frame);
  }

function vue_initiale_scene (pTpl, pScene)
  {
    return normalise_vue (pScene && pScene.vue_defaut)
      || normalise_vue (pScene && pScene.svg_viewbox)
      || normalise_vue (pTpl && pTpl._lectureNativeViewBox)
      || {x : 0, y : 0, w : 1600, h : 900};
  }

function vue_zoom_carte (pTpl, pCarte)
  {
    const tBase = normalise_vue (pTpl && pTpl._lectureNativeViewBox)
      || normalise_vue (pTpl && pTpl._lectureViewBox)
      || {x : 0, y : 0, w : 1600, h : 900};
    const tCarte = normalise_vue (pCarte && pCarte.vue);
    if (!tCarte) return null;
    const tRatio = tBase.w / tBase.h;
    let tW = tCarte.w / (1 - LECTURE_CARD_MARGIN * 2);
    let tH = tCarte.h / (1 - LECTURE_CARD_MARGIN * 2);
    if (tW / tH > tRatio) tH = tW / tRatio;
    else tW = tH * tRatio;
    const tCx = tCarte.x + tCarte.w / 2;
    const tCy = tCarte.y + tCarte.h / 2;
    return {
      x : tCx - tW / 2,
      y : tCy - tH / 2,
      w : tW,
      h : tH,
    };
  }

function restore_masked_nodes_tpl (pTpl)
  {
    const tMasked = pTpl && pTpl._lectureMaskedNodes;
    if (!Array.isArray (tMasked)) return;
    tMasked.forEach ((pItem) =>
      {
        if (!pItem || !pItem.node || !pItem.node.style) return;
        pItem.node.style.opacity = pItem.opacity || '';
        pItem.node.style.transition = pItem.transition || '';
      });
    pTpl._lectureMaskedNodes = [];
  }

function apply_card_masks_tpl (pTpl, pCarte)
  {
    restore_masked_nodes_tpl (pTpl);
    const tDoc = pTpl && pTpl._lectureSvgDoc;
    if (!tDoc || !pCarte) return;
    const tIds = Array.isArray (pCarte.ids_masques) ? pCarte.ids_masques : [];
    pTpl._lectureMaskedNodes = [];
    tIds.forEach ((pId) =>
      {
        const tNode = tDoc.getElementById (pId);
        if (!tNode || !tNode.style) return;
        pTpl._lectureMaskedNodes.push (
          {
            node : tNode,
            opacity : tNode.style.opacity,
            transition : tNode.style.transition,
          });
        tNode.style.transition = 'opacity 320ms ease';
        tNode.style.opacity = '0.08';
      });
  }

function clear_hover_tpl (pTpl)
  {
    const tHover = pTpl && pTpl._lectureHover;
    if (!tHover || !tHover.node || !tHover.node.style) return;
    tHover.node.style.filter = tHover.filter || '';
    tHover.node.style.transform = tHover.transform || '';
    tHover.node.style.transformOrigin = tHover.transformOrigin || '';
    tHover.node.style.transformBox = tHover.transformBox || '';
    tHover.node.style.transition = tHover.transition || '';
    pTpl._lectureHover = null;
  }

function apply_hover_tpl (pTpl, pInfo)
  {
    if (!pTpl) return;
    const tNode = pInfo && pInfo.node;
    if (pTpl._lectureHover && pTpl._lectureHover.node === tNode) return;
    clear_hover_tpl (pTpl);
    if (!tNode || !tNode.style || pInfo.type === 'card') return;
    const tReglages = lecture_settings ();
    const tContour = objet_config_scene (pInfo.id) || {};
    const tCouleur = (tContour.survol_contour && tContour.survol_contour.couleur)
      || tReglages.survol_contour_couleur
      || '#fefcdd';
    const tEpaisseur = Number ((tContour.survol_contour && tContour.survol_contour.epaisseur)
      || tReglages.survol_contour_epaisseur
      || 8);
    const tScale = 1 + (Number (tReglages.survol_obj_scale_pct) || 3) / 100;
    pTpl._lectureHover =
      {
        node : tNode,
        filter : tNode.style.filter,
        transform : tNode.style.transform,
        transformOrigin : tNode.style.transformOrigin,
        transformBox : tNode.style.transformBox,
        transition : tNode.style.transition,
      };
    tNode.style.transformBox = 'fill-box';
    tNode.style.transformOrigin = 'center';
    tNode.style.transition = `filter 180ms ease, transform 180ms ease`;
    tNode.style.transform = `scale(${Number.isFinite (tScale) ? tScale : LECTURE_HOVER_SCALE})`;
    tNode.style.filter = `drop-shadow(0 0 ${Math.max (2, tEpaisseur)}px ${tCouleur})`;
  }

function injecte_styles_svg_lecture (pDoc)
  {
    const tRoot = pDoc && pDoc.documentElement;
    if (!tRoot || pDoc.getElementById (LECTURE_STYLE_ID)) return;
    const tStyle = svg_el ('style');
    tStyle.setAttribute ('id', LECTURE_STYLE_ID);
    tStyle.textContent = `
      .diffusion-lecture-bubble { cursor: pointer; transition: opacity 260ms ease; }
      .diffusion-lecture-bubble.is-dimmed { opacity: .22; }
      .diffusion-lecture-bubble-shadow { pointer-events: none; }
      .diffusion-lecture-bubble-visual { filter: none; }
      .diffusion-lecture-bubble text { pointer-events: none; font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif; font-weight: 800; }
    `;
    tRoot.insertBefore (tStyle, tRoot.firstChild || null);
  }

function arrete_flottement_bulles_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._lectureBubblesRaf) cancelAnimationFrame (pTpl._lectureBubblesRaf);
    pTpl._lectureBubblesRaf = null;
    pTpl._lectureBubbleStates = [];
  }

function reglages_flottement_bulles ()
  {
    const tReglages = lecture_bubble_settings ();
    return {
      vitesse_deplacement : Math.max (0, Number (tReglages.lecture_flottement_vitesse_deplacement) || 0),
      max_x               : Math.max (0, Number (tReglages.lecture_flottement_deplacement_max_x) || 0),
      max_y               : Math.max (0, Number (tReglages.lecture_flottement_deplacement_max_y) || 0),
      vitesse_taille      : Math.max (0, Number (tReglages.lecture_flottement_vitesse_taille) || 0),
      taille_min          : Math.max (0.1, (Number (tReglages.lecture_flottement_taille_min_pct) || 100) / 100),
      taille_max          : Math.max (0.1, (Number (tReglages.lecture_flottement_taille_max_pct) || 100) / 100),
      vitesse_rotation    : Math.max (0, Number (tReglages.lecture_flottement_vitesse_rotation) || 0),
      rotation_min        : (Number (tReglages.lecture_flottement_rotation_min_pct) || 100) / 100,
      rotation_max        : (Number (tReglages.lecture_flottement_rotation_max_pct) || 100) / 100,
    };
  }

function transform_bulle_lecture (pEtat, pNow)
  {
    const tReglages = pEtat.reglages;
    const tTemps = pNow / 1000;
    const tDx = tReglages.max_x
      ? Math.sin (tTemps * tReglages.vitesse_deplacement * pEtat.freq_x + pEtat.phase_x) * tReglages.max_x
      : 0;
    const tDy = tReglages.max_y
      ? Math.sin (tTemps * tReglages.vitesse_deplacement * pEtat.freq_y + pEtat.phase_y) * tReglages.max_y
      : 0;

    const tScalePhase = (Math.sin (tTemps * tReglages.vitesse_taille * pEtat.freq_scale + pEtat.phase_scale) + 1) / 2;
    let tScale = tReglages.taille_min + (tReglages.taille_max - tReglages.taille_min) * tScalePhase;

    if (pEtat.press_until && pNow < pEtat.press_until)
      {
        const t = 1 - ((pEtat.press_until - pNow) / LECTURE_BUBBLE_PRESS_MS);
        const tRebond = t < 0.35
          ? 1 - 0.12 * (t / 0.35)
          : 0.88 + 0.16 * Math.sin ((t - 0.35) / 0.65 * Math.PI);
        tScale *= tRebond;
      }

    const tRotationBase = pEtat.rotation;
    const tRotationFacteur = tReglages.rotation_min
      + (tReglages.rotation_max - tReglages.rotation_min)
        * ((Math.sin (tTemps * tReglages.vitesse_rotation * pEtat.freq_rotation + pEtat.phase_rotation) + 1) / 2);
    const tRotation = tRotationBase * tRotationFacteur;
    return `translate(${pEtat.x + tDx} ${pEtat.y + tDy}) rotate(${tRotation}) scale(${tScale})`;
  }

function applique_flottement_bulles_tpl (pTpl, pNow)
  {
    const tEtats = pTpl && pTpl._lectureBubbleStates;
    if (!Array.isArray (tEtats) || !tEtats.length)
      {
        if (pTpl) pTpl._lectureBubblesRaf = null;
        return;
      }
    tEtats.forEach ((pEtat) =>
      {
        if (pEtat.node) pEtat.node.setAttribute ('transform', transform_bulle_lecture (pEtat, pNow));
      });
    pTpl._lectureBubblesRaf = requestAnimationFrame ((pFrameNow) => applique_flottement_bulles_tpl (pTpl, pFrameNow));
  }

function demarre_flottement_bulles_tpl (pTpl)
  {
    if (!pTpl || !Array.isArray (pTpl._lectureBubbleStates) || !pTpl._lectureBubbleStates.length) return;
    if (pTpl._lectureBubblesRaf) cancelAnimationFrame (pTpl._lectureBubblesRaf);
    pTpl._lectureBubblesRaf = requestAnimationFrame ((pNow) => applique_flottement_bulles_tpl (pTpl, pNow));
  }

function pulse_bulle_tpl (pTpl, pCarteId)
  {
    const tEtat = pTpl && Array.isArray (pTpl._lectureBubbleStates)
      ? pTpl._lectureBubbleStates.find ((pItem) => pItem.carte_id === pCarteId)
      : null;
    if (!tEtat) return;
    tEtat.press_until = performance.now () + LECTURE_BUBBLE_PRESS_MS;
    if (!pTpl._lectureBubblesRaf) demarre_flottement_bulles_tpl (pTpl);
  }

function dessine_bulles_svg_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._lectureSvgDoc;
    const tRoot = pTpl && pTpl._lectureSvgRoot;
    const tScene = scene_active ();
    if (!tDoc || !tRoot || !tScene) return;
    const tAncien = tDoc.getElementById (LECTURE_BUBBLES_LAYER_ID);
    if (tAncien && tAncien.parentNode) tAncien.parentNode.removeChild (tAncien);

    const tLayer = svg_el ('g');
    tLayer.setAttribute ('id', LECTURE_BUBBLES_LAYER_ID);
    tLayer.setAttribute ('class', 'diffusion-lecture-bubbles-layer');
    tRoot.appendChild (tLayer);
    arrete_flottement_bulles_tpl (pTpl);
    const tReglagesFlottement = reglages_flottement_bulles ();
    pTpl._lectureBubbleStates = [];

    cartes_scene (tScene).forEach ((pCarte, pIndex) =>
      {
        const tPos = position_bulle_carte (pCarte);
        if (!tPos) return;
        const tTaille = taille_bulle_carte (pCarte);
        const tRayon = Math.max (18, tTaille / 2);
        const tG = svg_el ('g');
        tG.setAttribute ('class', 'diffusion-lecture-bubble');
        const tCarteId = id_carte_lecture (pCarte);
        tG.setAttribute ('data-diffusion-card-id', tCarteId);
        tG.setAttribute ('transform', `translate(${tPos.x} ${tPos.y}) rotate(${Number (pCarte.rotation_bulle) || 0})`);
        pTpl._lectureBubbleStates.push (
          {
            carte_id       : tCarteId,
            node           : tG,
            x              : tPos.x,
            y              : tPos.y,
            rotation       : Number (pCarte.rotation_bulle) || 0,
            reglages       : tReglagesFlottement,
            freq_x         : 0.62 + ratio_hash_lecture (`${tCarteId}:fx`) * 0.55,
            freq_y         : 0.68 + ratio_hash_lecture (`${tCarteId}:fy`) * 0.55,
            freq_scale     : 0.55 + ratio_hash_lecture (`${tCarteId}:fs`) * 0.45,
            freq_rotation  : 0.5 + ratio_hash_lecture (`${tCarteId}:fr`) * 0.5,
            phase_x        : ratio_hash_lecture (`${tCarteId}:px`) * Math.PI * 2,
            phase_y        : ratio_hash_lecture (`${tCarteId}:py`) * Math.PI * 2,
            phase_scale    : ratio_hash_lecture (`${tCarteId}:ps`) * Math.PI * 2,
            phase_rotation : ratio_hash_lecture (`${tCarteId}:pr`) * Math.PI * 2,
            press_until    : 0,
          });

        const tHit = svg_el ('circle');
        tHit.setAttribute ('r', String (tRayon + 12));
        tHit.setAttribute ('fill', 'transparent');
        tHit.setAttribute ('data-diffusion-card-id', tCarteId);

        const tUrlOmbre = url_ombre_bulle_carte (pCarte);
        if (tUrlOmbre)
          {
            const tReglagesBulles = lecture_bubble_settings ();
            const tBlur = Math.max (0, Number (tReglagesBulles.ombre_blur) || 0);
            const tX = Number (tReglagesBulles.ombre_x) || 0;
            const tY = Number (tReglagesBulles.ombre_y) || 0;
            const tMarge = Math.ceil (tBlur * 4 + Math.max (Math.abs (tX), Math.abs (tY), 8));
            const tShadow = svg_el ('image');
            tShadow.setAttribute ('class', 'diffusion-lecture-bubble-shadow');
            tShadow.setAttribute ('href', tUrlOmbre);
            tShadow.setAttributeNS ('http://www.w3.org/1999/xlink', 'href', tUrlOmbre);
            tShadow.setAttribute ('x', String (-tRayon - tMarge));
            tShadow.setAttribute ('y', String (-tRayon - tMarge));
            tShadow.setAttribute ('width', String (tRayon * 2 + tMarge * 2));
            tShadow.setAttribute ('height', String (tRayon * 2 + tMarge * 2));
            tShadow.setAttribute ('preserveAspectRatio', 'none');
            tShadow.style.pointerEvents = 'none';
            tG.appendChild (tShadow);
          }

        let tVisual;
        const tUrlBulle = url_svg_bulle_carte (pCarte);
        if (tUrlBulle)
          {
            tVisual = svg_el ('image');
            tVisual.setAttribute ('class', 'diffusion-lecture-bubble-visual');
            tVisual.setAttribute ('href', tUrlBulle);
            tVisual.setAttributeNS ('http://www.w3.org/1999/xlink', 'href', tUrlBulle);
            tVisual.setAttribute ('x', String (-tRayon));
            tVisual.setAttribute ('y', String (-tRayon));
            tVisual.setAttribute ('width', String (tRayon * 2));
            tVisual.setAttribute ('height', String (tRayon * 2));
            tVisual.setAttribute ('preserveAspectRatio', 'xMidYMid meet');
            tVisual.setAttribute ('opacity', String (Number (pCarte.opacite_bulle) || 1));
          }
        else
          {
            tVisual = svg_el ('circle');
            tVisual.setAttribute ('class', 'diffusion-lecture-bubble-visual');
            tVisual.setAttribute ('r', String (tRayon));
            tVisual.setAttribute ('fill', couleur_bulle_carte (pCarte));
            tVisual.setAttribute ('opacity', String (Number (pCarte.opacite_bulle) || 1));
          }

        const tText = svg_el ('text');
        tText.setAttribute ('text-anchor', 'middle');
        tText.setAttribute ('dominant-baseline', 'central');
        tText.setAttribute ('fill', '#ffffff');
        tText.setAttribute ('font-size', String (Math.max (18, tRayon * 0.55)));
        tText.textContent = String (pIndex + 1);

        tG.appendChild (tHit);
        tG.appendChild (tVisual);
        tG.appendChild (tText);
        tLayer.appendChild (tG);
      });
    demarre_flottement_bulles_tpl (pTpl);
  }

function applique_focus_bulles_tpl (pTpl)
  {
    const tDoc = pTpl && pTpl._lectureSvgDoc;
    if (!tDoc || !tDoc.querySelectorAll) return;
    const tFocusId = pTpl.focusCarteId && pTpl.focusCarteId.get ();
    tDoc.querySelectorAll ('.diffusion-lecture-bubble').forEach ((pNode) =>
      {
        const tCarteId = pNode.getAttribute ('data-diffusion-card-id');
        if (tFocusId && tCarteId !== tFocusId) pNode.classList.add ('is-dimmed');
        else pNode.classList.remove ('is-dimmed');
      });
  }

function initialise_svg_lecture_tpl (pTpl, pObject)
  {
    const tScene = scene_active ();
    if (!pTpl || !pObject || !tScene) return;
    const tDoc = document_svg_depuis_object_lecteur (pObject);
    const tRoot = tDoc && tDoc.documentElement;
    if (!tDoc || !tRoot) return;

    pTpl._lectureSvgDoc = tDoc;
    pTpl._lectureSvgRoot = tRoot;
    injecte_styles_svg_lecture (tDoc);

    const tNative = normalise_vue (tScene.svg_viewbox)
      || parse_viewbox (tRoot.getAttribute ('viewBox'))
      || {
        x : 0,
        y : 0,
        w : Number (tRoot.getAttribute ('width')) || 1600,
        h : Number (tRoot.getAttribute ('height')) || 900,
      };
    pTpl._lectureNativeViewBox = tNative;
    set_svg_viewbox (pTpl, vue_initiale_scene (pTpl, tScene));

    const tIds = ids_svg_scene (tScene);
    pTpl._lectureIds = tIds;
    tIds.forEach ((pId) =>
      {
        const tNode = tDoc.getElementById (pId);
        if (tNode && tNode.style) tNode.style.cursor = 'pointer';
      });

    dessine_bulles_svg_tpl (pTpl);
    applique_focus_bulles_tpl (pTpl);

    if (pTpl._lectureSvgCleanup) pTpl._lectureSvgCleanup ();
    const pointerMove = (pEvent) =>
      {
        const tInfo = node_id_depuis_event (pEvent, tRoot, tIds);
        apply_hover_tpl (pTpl, tInfo && tInfo.type === 'object' ? tInfo : null);
      };
    const pointerDown = (pEvent) =>
      {
        const tInfo = node_id_depuis_event (pEvent, tRoot, tIds);
        if (!tInfo) return;
        if (!pTpl._lectureAudioPlayer) pTpl._lectureAudioPlayer = create_lecture_audio_player ();
        pTpl._lectureAudioPlayer.unlock ();
        start_ambiance_tpl (pTpl);
        if (tInfo.type === 'card')
          {
            pulse_bulle_tpl (pTpl, tInfo.id);
            const tAppareil = project_audio ('son_appareil_photo');
            if (tAppareil) play_audio_url_tpl (pTpl, audio_entry_url (tAppareil), {entry : tAppareil});
          }
        else
          {
            const tClic = project_audio ('son_clic');
            if (tClic) play_audio_url_tpl (pTpl, audio_entry_url (tClic), {entry : tClic});
          }
      };
    const pointerLeave = () => clear_hover_tpl (pTpl);
    const click = (pEvent) =>
      {
        const tInfo = node_id_depuis_event (pEvent, tRoot, tIds);
        if (!tInfo) return;
        pEvent.preventDefault ();
        pEvent.stopPropagation ();
        if (tInfo.type === 'card')
          setTimeout (() => focus_carte_lecture_tpl (pTpl, tInfo.id), 140);
        else gere_clic_objet_lecture_tpl (pTpl, tInfo.id);
      };
    tDoc.addEventListener ('pointermove', pointerMove);
    tDoc.addEventListener ('pointerdown', pointerDown);
    tDoc.addEventListener ('pointerleave', pointerLeave);
    tDoc.addEventListener ('click', click);
    pTpl._lectureSvgCleanup = () =>
      {
        tDoc.removeEventListener ('pointermove', pointerMove);
        tDoc.removeEventListener ('pointerdown', pointerDown);
        tDoc.removeEventListener ('pointerleave', pointerLeave);
        tDoc.removeEventListener ('click', click);
      };
  }

function reset_svg_lecture_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._sceneTransitionAvantTimer) clearTimeout (pTpl._sceneTransitionAvantTimer);
    if (pTpl._sceneTransitionApresTimer) clearTimeout (pTpl._sceneTransitionApresTimer);
    if (pTpl._lectureObjectLoadCleanup) pTpl._lectureObjectLoadCleanup ();
    if (pTpl._lectureSvgCleanup) pTpl._lectureSvgCleanup ();
    if (pTpl._lectureViewBoxRaf) cancelAnimationFrame (pTpl._lectureViewBoxRaf);
    pTpl._lectureViewBoxAnimationId = (pTpl._lectureViewBoxAnimationId || 0) + 1;
    pTpl._sceneTransitionAvantTimer = null;
    pTpl._sceneTransitionApresTimer = null;
    arrete_flottement_bulles_tpl (pTpl);
    pTpl._lectureObjectLoadCleanup = null;
    pTpl._lectureObjectNode = null;
    pTpl._lectureObjectUrl = null;
    pTpl._lectureSvgCleanup = null;
    pTpl._lectureViewBoxRaf = null;
    pTpl._lectureSvgDoc = null;
    pTpl._lectureSvgRoot = null;
    pTpl._lectureNativeViewBox = null;
    pTpl._lectureViewBox = null;
    pTpl._lectureIds = new Set ();
    pTpl.focusCarteId && pTpl.focusCarteId.set (null);
    pTpl.progressCarte && pTpl.progressCarte.set ({});
    clear_hover_tpl (pTpl);
    restore_masked_nodes_tpl (pTpl);
    stop_audio_tpl (pTpl);
  }

function termine_transition_reader_apres_rendu_tpl (pTpl, pSceneSlug, pFadeMs)
  {
    if (!pTpl) return;
    const tSceneSlug = typeof pSceneSlug === 'string' ? pSceneSlug : '';
    const tFadeMs = Math.max (120, Number (pFadeMs) || 0);
    const tDebutMs = Date.now ();
    const tAttenteMaxMs = Math.max (700, Math.round (tFadeMs * 3));
    const tRaf = typeof requestAnimationFrame === 'function'
      ? requestAnimationFrame
      : (pCb) => setTimeout (pCb, 16);
    const tProgrammeFadeIn = () =>
      {
        pTpl._sceneTransitionApresTimer = setTimeout (() =>
          {
            pTpl._sceneTransitionApresTimer = null;
            pTpl.transitionActive && pTpl.transitionActive.set (false);
          }, tFadeMs);
      };
    const tAttendRendu = () =>
      {
        const tRuntime = pTpl.find && pTpl.find ('.js_hub_scene_runtime');
        const tSceneNode = tRuntime && tRuntime.querySelector && tRuntime.querySelector ('.scene');
        const tObject = tSceneNode && tSceneNode.querySelector && tSceneNode.querySelector ('object');
        const tBonneScene = !tSceneSlug || Session.get ('scene_slug') === tSceneSlug;
        const tSvgCharge = !tObject
          || !('contentDocument' in tObject)
          || !!document_svg_depuis_object_lecteur (tObject);
        const tObjectVisible = !!(
          tObject
          && tObject.getAttribute ('data')
          && tSvgCharge
          && window.getComputedStyle (tObject).visibility !== 'hidden'
          && window.getComputedStyle (tObject).display !== 'none'
          && window.getComputedStyle (tObject).opacity !== '0'
        );
        const tTimeout = Date.now () - tDebutMs >= tAttenteMaxMs;
        if ((tBonneScene && tObjectVisible) || tTimeout)
          {
            tRaf (() => tRaf (tProgrammeFadeIn));
            return;
          }
        pTpl._sceneTransitionApresTimer = setTimeout (tAttendRendu, 40);
      };
    tAttendRendu ();
  }

function annule_transition_reader_tpl (pTpl)
  {
    if (!pTpl) return;
    if (pTpl._sceneTransitionAvantTimer) clearTimeout (pTpl._sceneTransitionAvantTimer);
    if (pTpl._sceneTransitionApresTimer) clearTimeout (pTpl._sceneTransitionApresTimer);
    pTpl._sceneTransitionAvantTimer = null;
    pTpl._sceneTransitionApresTimer = null;
    pTpl.transitionActive && pTpl.transitionActive.set (false);
  }

function navigate_scene_lecture_tpl (pTpl, pSceneIdOrSlug)
  {
    if (scene_cible_verrouillee_diffusion (pSceneIdOrSlug))
      {
        if (typeof window.cogibulleDiffusionAccessGate === 'function')
          window.cogibulleDiffusionAccessGate ({action : 'scene', scene_id : pSceneIdOrSlug});
        return true;
      }
    const tScene = scene_par_id_ou_slug (pSceneIdOrSlug);
    if (!tScene) return false;
    annule_transition_reader_tpl (pTpl);
    pTpl.cartesReussies && pTpl.cartesReussies.set ({});
    clear_hype_tpl (pTpl);
    stop_audio_tpl (pTpl);
    clear_hover_tpl (pTpl);
    if (ouvre_scene_lecture_avec_fondu (null, tScene.id || tScene._id))
      {
        signale_evenement_lecture ('scene');
        return true;
      }
    Session.set ('scene_slug', tScene.slug);
    signale_evenement_lecture ('scene');
    return true;
  }

function focus_carte_lecture_tpl (pTpl, pCarteId)
  {
    const tScene = scene_active ();
    const tCarte = carte_scene (pCarteId, tScene);
    if (!pTpl || !tScene || !tCarte) return false;
    pTpl.focusCarteId.set (pCarteId);
    pTpl.progressCarte.set ({});
    apply_card_masks_tpl (pTpl, tCarte);
    applique_focus_bulles_tpl (pTpl);
    const tVue = vue_zoom_carte (pTpl, tCarte);
    anime_viewbox_tpl (pTpl, tVue || vue_initiale_scene (pTpl, tScene), lecture_settings ().zoom_carte_duree_ms);
    const tPremierObjetOui = carte_objets_oui (tCarte)[0];
    const tPremierConfig = tPremierObjetOui && objet_config_scene (tPremierObjetOui, tScene);
    play_audio_sequence_tpl (pTpl, scene_entends_tu_sequence (tScene, tPremierConfig));
    signale_evenement_lecture ('card-focus', pCarteId);
    return true;
  }

function quitte_focus_lecture_tpl (pTpl, pOptions = {})
  {
    const tScene = scene_active ();
    if (!pTpl || !tScene) return;
    pTpl.focusCarteId.set (null);
    pTpl.progressCarte.set ({});
    restore_masked_nodes_tpl (pTpl);
    applique_focus_bulles_tpl (pTpl);
    anime_viewbox_tpl (
      pTpl,
      vue_initiale_scene (pTpl, tScene),
      lecture_settings ().zoom_carte_duree_ms,
      {onComplete : pOptions && pOptions.onComplete}
    );
    const tRetour = project_audio ('son_retour_menu');
    if (tRetour) play_audio_url_tpl (pTpl, audio_entry_url (tRetour), {entry : tRetour});
  }

function gere_reponse_objet_focus_tpl (pTpl, pObjetId, pConfig)
  {
    const tFocusId = pTpl && pTpl.focusCarteId && pTpl.focusCarteId.get ();
    const tScene = scene_active ();
    const tCarte = tFocusId && carte_scene (tFocusId, tScene);
    if (!tCarte) return false;
    const tOrdre = Array.isArray (tCarte.ordre_objets) ? tCarte.ordre_objets : [];
    if (!tOrdre.includes (pObjetId)) return false;

    const tCorrect = /son[-_]oui/i.test (pObjetId);
    const tProgress = {...(pTpl.progressCarte.get () || {})};
    const tDejaValide = !!tProgress[pObjetId];
    if (tCorrect) tProgress[pObjetId] = true;
    pTpl.progressCarte.set (tProgress);

    const tSequence = pConfig && pConfig.audio_url
      ? [{url : pConfig.audio_url}, project_audio (tCorrect ? 'son_reussite' : 'son_echec')]
      : [project_audio (tCorrect ? 'son_reussite' : 'son_echec')];
    play_audio_sequence_tpl (pTpl, tSequence);
    const tCarteId = id_carte_lecture (tCarte);
    signale_evenement_lecture (tCorrect ? 'object-ok' : 'object-ko', tCarteId);

    const tOui = carte_objets_oui (tCarte);
    if (tCorrect && !tDejaValide && tOui.length && tOui.every ((pId) => tProgress[pId]))
      {
        const tReussies = {...(pTpl.cartesReussies && pTpl.cartesReussies.get () || {})};
        const tCarteDejaReussie = !!tReussies[tCarteId];
        tReussies[tCarteId] = true;
        const tCartesEvaluables = cartes_scene (tScene).filter ((pCarte) => carte_objets_oui (pCarte).length > 0);
        const tSceneKey = `scene:${(tScene && (tScene.id || tScene._id)) || ''}`;
        const tSceneDejaReussie = !!tReussies[tSceneKey];
        const tSceneReussie = tCartesEvaluables.length > 0
          && tCartesEvaluables.every ((pCarte) => tReussies[id_carte_lecture (pCarte)]);
        if (tSceneReussie) tReussies[tSceneKey] = true;
        pTpl.cartesReussies && pTpl.cartesReussies.set (tReussies);
        setTimeout (() =>
          {
            const tReussite = project_audio ('musique_animation_liane')
              || project_audio ('musique_pluie_de_bananes')
              || project_audio ('son_reussite');
            if (tReussite) play_audio_url_tpl (pTpl, audio_entry_url (tReussite), {entry : tReussite});
            const lanceRecompenseScene = () =>
              {
                if (!tSceneReussie || tSceneDejaReussie) return;
                const tSceneCourante = scene_active ();
                if (!tSceneCourante || ((tSceneCourante.id || tSceneCourante._id) !== (tScene.id || tScene._id))) return;
                const tReussiteScene = project_audio ('musique_pluie_de_bananes');
                if (tReussiteScene) play_audio_url_tpl (pTpl, audio_entry_url (tReussiteScene), {entry : tReussiteScene});
                if (!show_hype_tpl (pTpl, 'recompense_scene', {onDone : () => execute_retour_menu_lecture_tpl (pTpl)}))
                  execute_retour_menu_lecture_tpl (pTpl);
              };
            const quitteDerniereCarte = () =>
              quitte_focus_lecture_tpl (pTpl, {onComplete : lanceRecompenseScene});
            if (tSceneReussie && !tSceneDejaReussie)
              {
                if (!tCarteDejaReussie && show_hype_tpl (pTpl, 'recompense_carte', {onDone : quitteDerniereCarte}))
                  return;
                quitteDerniereCarte ();
                return;
              }
            if (!tCarteDejaReussie) show_hype_tpl (pTpl, 'recompense_carte');
          }, 420);
      }
    return true;
  }

function gere_clic_objet_lecture_tpl (pTpl, pObjetId)
  {
    const tScene = scene_active ();
    const tConfig = objet_config_scene (pObjetId, tScene) || {};
    if (gere_reponse_objet_focus_tpl (pTpl, pObjetId, tConfig)) return true;

    const tAction = tConfig.interactivite_action;
    if (tAction === 'scene' && tConfig.interactivite_scene_id)
      return navigate_scene_lecture_tpl (pTpl, tConfig.interactivite_scene_id);
    if (tAction === 'url' && tConfig.interactivite_url)
      {
        window.open (tConfig.interactivite_url, '_blank', 'noopener');
        return true;
      }
    if ((tAction === 'sound' || tAction === 'son') && tConfig.audio_url)
      {
        play_audio_url_tpl (pTpl, tConfig.audio_url);
        return true;
      }
    if (tAction === 'account')
      {
        ouvre_overlay_compte_diffusion ();
        return true;
      }
    if (tConfig.audio_url)
      {
        play_audio_url_tpl (pTpl, tConfig.audio_url);
        return true;
      }
    const tSceneCible = tConfig.interactivite_scene_id && scene_par_id_ou_slug (tConfig.interactivite_scene_id);
    if (tSceneCible) return navigate_scene_lecture_tpl (pTpl, tSceneCible.slug);
    return false;
  }

function execute_retour_menu_lecture_tpl (pTpl)
  {
    const tRetour = bouton_retour_lecture_scene ();
    if (!tRetour || !tRetour.id) return false;
    return gere_clic_objet_lecture_tpl (pTpl, tRetour.id);
  }

FlowRouter.route ('/',
  {
    name : 'diffusion.catalogue',
    action ()
      {
        Session.set ('route', 'catalogue');
        Session.set ('version_id', null);
        Session.set ('scene_slug', null);
        Session.set ('public_slug', null);
        Session.set ('diffusion_mode_test', route_mode_test_diffusion ());
      },
  });

FlowRouter.route ('/jouer/:pVersionId',
  {
    name : 'diffusion.lecteur',
    action (pParams)
      {
        const tVersionId = decodeURIComponent (pParams.pVersionId);
        Session.set ('route', 'lecteur');
        Session.set ('version_id', tVersionId);
        Session.set ('scene_slug', null);
        Session.set ('public_slug', null);
        Session.set ('diffusion_mode_test', garde_mode_test_diffusion ());
      },
  });

FlowRouter.route ('/jouer/:pVersionId/:pSceneSlug',
  {
    name : 'diffusion.lecteur.scene',
    action (pParams)
      {
        const tVersionId = decodeURIComponent (pParams.pVersionId);
        Session.set ('route', 'lecteur');
        Session.set ('version_id', tVersionId);
        Session.set ('scene_slug', decodeURIComponent (pParams.pSceneSlug));
        Session.set ('public_slug', null);
        Session.set ('diffusion_mode_test', garde_mode_test_diffusion ());
      },
  });

FlowRouter.route ('/compte',
  {
    name : 'diffusion.compte',
    action ()
      {
        Session.set ('route', 'compte');
        Session.set ('public_slug', null);
        Session.set ('diffusion_mode_test', route_mode_test_diffusion ());
      },
  });

FlowRouter.route ('/auth/passwordless/:pToken',
  {
    name : 'diffusion.passwordless.confirm',
    action (pParams)
      {
        const tToken = decodeURIComponent (pParams.pToken || '');
        const tSelector = FlowRouter.getQueryParam ('selector') || '';
        const tVersionId = (FlowRouter.getQueryParam ('version_id') || '').trim ();
        gPasswordlessVersionId = tVersionId || null;
        // Priorite au chemin de retour fourni par le lien (le serveur y met
        // l'alias public quand il existe) ; /jouer/<version_id> en secours.
        gPasswordlessReturnPath = chemin_local_diffusion_valide (FlowRouter.getQueryParam ('retour') || '', '')
          || chemin_entree_passwordless_diffusion (tVersionId, '/');
        connecte_passwordless_depuis_lien (tSelector, tToken, tVersionId);
        FlowRouter.go (gPasswordlessReturnPath || '/');
      },
  });

FlowRouter.route ('/auth/reset-password/:pToken',
  {
    name : 'diffusion.password.reset',
    action (pParams)
      {
        const tVersionId = (FlowRouter.getQueryParam ('version_id') || '').trim ();
        Session.set ('route', 'reset_password');
        Session.set ('public_slug', null);
        Session.set ('diffusion_mode_test', route_mode_test_diffusion ());
        Session.set ('reset_password_token', decodeURIComponent (pParams.pToken || ''));
        Session.set ('reset_password_version_id', tVersionId || null);
        gPasswordlessVersionId = tVersionId || null;
      },
  });

FlowRouter.route ('/:pPublicSlug',
  {
    name : 'diffusion.alias',
    action (pParams)
      {
        const tPublicSlug = normalise_slug_public_diffusion (decodeURIComponent (pParams.pPublicSlug || ''));
        Session.set ('route', 'lecteur');
        Session.set ('version_id', version_id_alias_public_local (tPublicSlug));
        Session.set ('scene_slug', null);
        Session.set ('public_slug', tPublicSlug);
        Session.set ('diffusion_mode_test', route_mode_test_diffusion ());
      },
  });

FlowRouter.route ('/:pPublicSlug/:pSceneSlug',
  {
    name : 'diffusion.alias.scene',
    action (pParams)
      {
        const tPublicSlug = normalise_slug_public_diffusion (decodeURIComponent (pParams.pPublicSlug || ''));
        Session.set ('route', 'lecteur');
        Session.set ('version_id', version_id_alias_public_local (tPublicSlug));
        Session.set ('scene_slug', decodeURIComponent (pParams.pSceneSlug || ''));
        Session.set ('public_slug', tPublicSlug);
        Session.set ('diffusion_mode_test', route_mode_test_diffusion ());
      },
  });

// Mode embarqué (appli native) : démarrer le runtime local au boot, puis
// entrer directement dans le lecteur — l'appli n'a ni catalogue ni compte.
// ---------------------------------------------------------------------------
// Coupure DDP des onglets masqués (12/09/2026, chantier mémoire des sessions).
//
// Un onglet laissé ouvert garde sa session DDP, ses abonnements et sa merge
// box côté serveur pendant des heures (trois sessions ont tenu toute une nuit
// avec 800 documents chacune). Après dix minutes d'onglet masqué, on coupe
// la connexion : le serveur libère tout. Au retour, on reconnecte : Meteor
// rejoue les abonnements et les autoruns repartent comme après une coupure
// réseau. Sans objet en mode embarqué, où il n'y a pas de serveur.
// ---------------------------------------------------------------------------

const DELAI_COUPURE_ONGLET_MASQUE_MS = 10 * 60 * 1000;
let gTimerCoupureOngletMasque = null;

Meteor.startup (() =>
  {
    if (est_runtime_embarque ()) return;
    if (typeof document === 'undefined' || typeof document.addEventListener !== 'function') return;
    document.addEventListener ('visibilitychange', () =>
      {
        if (document.visibilityState === 'hidden')
          {
            if (gTimerCoupureOngletMasque) return;
            gTimerCoupureOngletMasque = setTimeout (() =>
              {
                gTimerCoupureOngletMasque = null;
                if (document.visibilityState !== 'hidden') return;
                Meteor.disconnect ();
              }, DELAI_COUPURE_ONGLET_MASQUE_MS);
            return;
          }
        if (gTimerCoupureOngletMasque)
          {
            clearTimeout (gTimerCoupureOngletMasque);
            gTimerCoupureOngletMasque = null;
          }
        Meteor.reconnect ();
      });
  });

Meteor.startup (() =>
  {
    if (!demarre_runtime_embarque ()) return;
    Tracker.autorun (() =>
      {
        if (!runtime_embarque_pret ()) return;
        const tVersionId = version_id_embarque ();
        if (!tVersionId) return;
        if (Session.get ('route') !== 'lecteur')
          FlowRouter.go ('/jouer/' + encodeURIComponent (tVersionId));
      });
  });

Template.diffusion_app.onCreated (function ()
  {
    this.autorun (() =>
      {
        // Mode embarqué (appli native) : pas de serveur, donc aucune
        // subscription — les données viennent du manifest embarqué, hydraté
        // dans Minimongo par le runtime (client/imports/runtime_embarque.js).
        if (est_runtime_embarque ())
          {
            const tPret = runtime_embarque_pret ();
            gCatalogueReady.set (tPret);
            gVersionDataReady.set (tPret);
            return;
          }

        this.subscribe ('diffusion.node');
        const tCatalogueSub = this.subscribe ('diffusion.catalogue', {session_id : user_session_id ()});
        gCatalogueReady.set (tCatalogueSub.ready ());

        const tPublicSlug = alias_public_route ();
        if (tPublicSlug)
          this.subscribe ('diffusion.aliasPublic', {slug : tPublicSlug});

        const tVersionId = version_id_route ();
        if (tVersionId)
          {
            const tVersionSub = this.subscribe ('diffusion.version',
            {
              version_id: tVersionId,
              session_id: session_id_version (tVersionId),
              user_session_id: user_session_id (),
              scene_slug: Session.get ('scene_slug') || '',
            });
            gVersionDataReady.set (tVersionSub.ready ());
          }
        else
          gVersionDataReady.set (false);
      });

    this.autorun (() =>
      {
        const tAlias = resout_alias_public_route ();
        if (!tAlias) return;
        if (Session.get ('version_id') !== tAlias.version_id)
          Session.set ('version_id', tAlias.version_id);
      });

    this._authMeteorKey = null;
    this.autorun (() =>
      {
        const tUserId = Meteor.userId ();
        const tLoggingIn = Meteor.loggingIn ();
        const tVersionId = version_id_route () || gPasswordlessVersionId || '';
        if (tLoggingIn)
          {
            gAuthReady.set (false);
            return;
          }
        if (!tUserId)
          {
            this._authMeteorKey = null;
            oublie_user_session ();
            gAuthReady.set (true);
            demande_etat_acces_diffusion ();
            return;
          }
        gCurrentUser.set (user_public_meteor_client ());
        Session.set ("diffusion_connecte", true);
        const tKey = tUserId + ':' + tVersionId;
        if (this._authMeteorKey === tKey) return;
        this._authMeteorKey = tKey;
        gAuthReady.set (false);
        assure_session_cogibulle_apres_login ({version_id : tVersionId});
      });

    this.autorun (() =>
      {
        applique_meta_partage_diffusion ();
      });

    this.autorun (() =>
      {
        applique_couleurs_page_diffusion ();
      });
  });

Template.diffusion_app.onRendered (function ()
  {
    // Retire le loader de démarrage injecté par le serveur dans le HTML
    // initial (server/serveur.js) : dès ce premier rendu, les états d'attente
    // Blaze (diffusion_chargement_boules) prennent le relais visuellement.
    const tLoader = document.getElementById ('diffusion_boot_loader');
    if (tLoader && tLoader.parentNode) tLoader.parentNode.removeChild (tLoader);
    const tStyle = document.getElementById ('diffusion_boot_loader_style');
    if (tStyle && tStyle.parentNode) tStyle.parentNode.removeChild (tStyle);
  });

// Helper global : utilisé dans plusieurs templates (lecteur, app…).
Template.registerHelper ('runtime_embarque_actif', () => est_runtime_embarque ());

Template.diffusion_app.helpers (
  {
    app_shell_class ()
      {
        const tRoute = Session.get ('route');
        if (tRoute === 'lecteur') return 'app_shell_lecteur';
        if (tRoute === 'compte') return 'app_shell_compte';
        return '';
      },

    app_shell_style ()
      {
        const tHeader = affichage_communication_courant ();
        const tVars = [];
        ajoute_variable_couleur_diffusion (tVars, 'browser-light', tHeader.browser_bg_light);
        ajoute_variable_couleur_diffusion (tVars, 'browser-dark', tHeader.browser_bg_dark);
        const tFondScene = fond_scene_embarque ();
        ajoute_variable_couleur_diffusion (tVars, 'page-light', tFondScene || tHeader.page_bg_light);
        ajoute_variable_couleur_diffusion (tVars, 'page-dark', tFondScene || tHeader.page_bg_dark);
        if (Session.get ('route') === 'lecteur' || Session.get ('route') === 'compte')
          {
            const tRatio = ratio_manifest_courant_num ();
            tVars.push (`--scene-frame-ratio: ${ratio_manifest_courant ()}`);
            tVars.push (`--scene-frame-ratio-num: ${tRatio}`);
            tVars.push (`--scene-frame-ratio-inverse-num: ${1 / tRatio}`);
          }
        return tVars.join ('; ') + ';';
      },

    route_lecteur ()
      {
        return Session.get ('route') === 'lecteur';
      },

    route_compte ()
      {
        return Session.get ('route') === 'compte';
      },

    route_reset_password ()
      {
        return Session.get ('route') === 'reset_password';
      },

    current_user ()
      {
        return gCurrentUser.get ();
      },

    current_user_label ()
      {
        const tUser = gCurrentUser.get ();
        return (tUser && tUser.profile && tUser.profile.name)
          || (tUser && tUser.email)
          || 'Compte connecté';
      },

    diffusion_toast ()
      {
        const tToast = gDiffusionToast.get ();
        return tToast && tToast.texte;
      },

    diffusion_toast_type ()
      {
        const tToast = gDiffusionToast.get ();
        return tToast && tToast.type === 'erreur' ? 'erreur' : 'succes';
      },

    diffusion_toast_style ()
      {
        const tToast = gDiffusionToast.get ();
        const tDurationMs = tToast && Number (tToast.duration_ms);
        if (!Number.isFinite (tDurationMs) || tDurationMs <= 0) return '';
        return `--diffusion-toast-duration-ms: ${tDurationMs}ms;`;
      },

    welcome_overlay_open ()
      {
        return !!gWelcomeOverlay.get ();
      },

    passwordless_sent_dialog_open ()
      {
        return gPasswordlessSentDialog.get ();
      },

    passwordless_sent_dialog_html ()
      {
        return html_message_passwordless_envoye ();
      },

    welcome_overlay_class ()
      {
        return gWelcomeOverlayClosing.get () ? 'is-closing' : 'is-open';
      },

    welcome_overlay_html ()
      {
        const tOverlay = gWelcomeOverlay.get ();
        return tOverlay && tOverlay.html;
      },

    dialogue_efface_local_ouvert ()
      {
        return gDialogueEffaceLocalOuvert.get ();
      },

    bandeau_cookies_visible ()
      {
        const tReglages = reglages_pixel_meta_courants ();
        if (!tReglages) return false;
        // Suivi sans consentement (tests marketing) : ni bandeau ni mur.
        if (tReglages.sans_consentement) return false;
        const tConsentement = gConsentementCookies.get ();
        // Consentement obligatoire : le mur reste affiché tant que
        // l'utilisateur n'a pas accepté, même après un refus enregistré.
        if (tReglages.consentement_obligatoire) return tConsentement !== 'accepte';
        return !tConsentement;
      },

    consentement_cookies_obligatoire ()
      {
        const tReglages = reglages_pixel_meta_courants ();
        return !!(tReglages && tReglages.consentement_obligatoire);
      },

    consentement_cookies_refuse ()
      {
        return gConsentementCookies.get () === 'refuse';
      },

    texte_bandeau_cookies ()
      {
        return texte_bandeau_cookies_courant ();
      },

  });

Template.diffusion_app.events (
  {
    'click .js_welcome_overlay_close' ()
      {
        ferme_overlay_bienvenue_diffusion ();
      },

    'click .js_diffusion_panel_close' (pEvent)
      {
        pEvent.preventDefault ();
        pEvent.stopPropagation ();
        FlowRouter.go ('/');
      },

    'keydown .js_welcome_overlay_close' (pEvent)
      {
        if (pEvent.key !== 'Escape' && pEvent.key !== 'Enter' && pEvent.key !== ' ') return;
        pEvent.preventDefault ();
        ferme_overlay_bienvenue_diffusion ();
      },

    'click .js_efface_local_annuler' (pEvent)
      {
        pEvent.preventDefault ();
        gDialogueEffaceLocalOuvert.set (false);
      },

    'click .js_efface_local_confirmer' (pEvent)
      {
        pEvent.preventDefault ();
        gDialogueEffaceLocalOuvert.set (false);
        try { localStorage.clear (); } catch (e) { /* stockage inaccessible */ }
        // Rechargement : l'état en mémoire (sessions, consentements) n'est
        // plus cohérent avec le stockage vidé.
        window.location.reload ();
      },

    'click .js_cookies_accepter' (pEvent)
      {
        pEvent.preventDefault ();
        enregistre_consentement_cookies ('accepte');
      },

    'click .js_cookies_refuser' (pEvent)
      {
        pEvent.preventDefault ();
        enregistre_consentement_cookies ('refuse');
      },

  });

Template.diffusion_tableau.helpers (
  {
    auth_ready ()
      {
        return gAuthReady.get ();
      },

    current_user ()
      {
        return gCurrentUser.get ();
      },

    node_games_count ()
      {
        const tNode = DiffusionNodeState.findOne (DIFFUSION_NODE_ID);
        return (tNode && tNode.games_count) || 0;
      },

    node_active_versions_count ()
      {
        const tNode = DiffusionNodeState.findOne (DIFFUSION_NODE_ID);
        return (tNode && tNode.active_versions_count) || 0;
      },

    node_sync_label ()
      {
        const tNode = DiffusionNodeState.findOne (DIFFUSION_NODE_ID);
        return tNode && tNode.sync_configured ? 'Configurée' : 'Locale';
      },

    node_last_sync_label ()
      {
        const tNode = DiffusionNodeState.findOne (DIFFUSION_NODE_ID);
        return date_label (tNode && tNode.last_sync_at);
      },

    catalogue_resume ()
      {
        const tNb = DiffusionVersions.find ({active : true, status : 'active'}).count ();
        return tNb + (tNb > 1 ? ' versions actives' : ' version active');
      },

    catalogue_chargement ()
      {
        return !gCatalogueReady.get ()
          || DiffusionVersions.find ({active : true, status : 'active'}).count () === 0;
      },

    versions_actives ()
      {
        return DiffusionVersions.find (
          {active : true, status : 'active'},
          {sort : {published_at : -1, game_slug : 1}}
        );
      },
  });

Template.diffusion_tableau.events (
  {
    'click .js_installe_demo' ()
      {
        Meteor.call ('diffusion.demo.installe');
      },
  });

Template.diffusion_account.helpers (
  {
    current_user ()
      {
        return gCurrentUser.get ();
      },

    current_user_email ()
      {
        const tUser = gCurrentUser.get ();
        return email_utilisateur_public (tUser) || 'Compte connecté';
      },

    account_password_enabled ()
      {
        return utilisateur_mot_de_passe_actif (gCurrentUser.get ());
      },

    account_password_form_visible ()
      {
        return Template.instance ().passwordFormVisible.get ();
      },

    account_message ()
      {
        const tMessage = Template.instance ().accountMessage.get ();
        return tMessage && tMessage.texte;
      },

    account_message_class ()
      {
        const tMessage = Template.instance ().accountMessage.get ();
        return tMessage && tMessage.type === 'succes' ? 'form_success' : 'form_error';
      },

    account_busy_attr ()
      {
        return Template.instance ().accountBusy.get () ? 'disabled' : null;
      },

    account_password_submit_label ()
      {
        if (Template.instance ().accountBusy.get ()) return 'Enregistrement...';
        return utilisateur_mot_de_passe_actif (gCurrentUser.get ())
          ? 'Changer le mot de passe'
          : 'Activer le mot de passe';
      },
  });

Template.diffusion_account.onCreated (function ()
  {
    this.passwordFormVisible = new ReactiveVar (false);
    this.accountMessage = new ReactiveVar (null);
    this.accountBusy = new ReactiveVar (false);
    this.autorun (() =>
      {
        if (Meteor.userId ()) rafraichit_etat_compte_diffusion ();
      });
  });

Template.diffusion_account.events (
  {
    'click .js_diffusion_panel_close' (pEvent)
      {
        pEvent.preventDefault ();
        pEvent.stopPropagation ();
        if (gAccountOverlayRendered.get ()) ferme_overlay_compte_diffusion ();
        else FlowRouter.go ('/');
      },

    'click .js_logout' (pEvent)
      {
        pEvent.preventDefault ();
        pEvent.stopPropagation ();
        deconnecte_compte_diffusion ();
      },

    'click .js_account_password_form_toggle' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        pTpl.accountMessage.set (null);
        pTpl.passwordFormVisible.set (!pTpl.passwordFormVisible.get ());
      },

    'submit .js_account_password_form' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        const tUser = gCurrentUser.get ();
        const tPasswordEnabled = utilisateur_mot_de_passe_actif (tUser);
        const tOldPassword = pTpl.find ('.js_account_old_password');
        const tNewPassword = pTpl.find ('.js_account_new_password');
        const tConfirmPassword = pTpl.find ('.js_account_confirm_password');
        const tNouveau = tNewPassword && tNewPassword.value || '';
        if (!mot_de_passe_compte_valide (tNouveau))
          {
            pTpl.accountMessage.set ({type : 'erreur', texte : 'Le mot de passe doit contenir au moins 8 caractères.'});
            return;
          }
        if (tNouveau !== (tConfirmPassword && tConfirmPassword.value || ''))
          {
            pTpl.accountMessage.set ({type : 'erreur', texte : 'La confirmation ne correspond pas au mot de passe.'});
            return;
          }
        pTpl.accountBusy.set (true);
        pTpl.accountMessage.set (null);
        const tCallback = (pErr, pRes) =>
          {
            pTpl.accountBusy.set (false);
            if (pErr)
              {
                pTpl.accountMessage.set (
                  {type : 'erreur', texte : message_erreur_compte (pErr, 'Mot de passe non enregistré.')}
                );
                return;
              }
            if (pRes && pRes.user) gCurrentUser.set (pRes.user);
            pTpl.passwordFormVisible.set (false);
            pTpl.accountMessage.set (
              {type : 'succes', texte : tPasswordEnabled ? 'Mot de passe modifié.' : 'Connexion par mot de passe activée.'}
            );
            if (tOldPassword) tOldPassword.value = '';
            if (tNewPassword) tNewPassword.value = '';
            if (tConfirmPassword) tConfirmPassword.value = '';
            rafraichit_etat_compte_diffusion ();
          };
        if (tPasswordEnabled)
          {
            Accounts.changePassword (tOldPassword && tOldPassword.value || '', tNouveau, tCallback);
            return;
          }
        Meteor.call ('diffusion.auth.enablePassword', {password : tNouveau}, tCallback);
      },

  });

Template.diffusion_login.onCreated (function ()
  {
    this.loginMessage = new ReactiveVar (null);
    this.loginBusy = new ReactiveVar (false);
    this.passwordMode = new ReactiveVar (false);
  });

Template.diffusion_login.helpers (
  {
    login_message ()
      {
        const tMessage = Template.instance ().loginMessage.get ();
        return tMessage && tMessage.texte;
      },

    login_message_class ()
      {
        const tMessage = Template.instance ().loginMessage.get ();
        return tMessage && tMessage.type === 'succes' ? 'form_success' : 'form_error';
      },

    login_intro_text ()
      {
        return Template.instance ().passwordMode.get ()
          ? 'Connectez-vous avec votre mot de passe.'
          : 'Indiquez votre adresse e-mail pour recevoir un lien de confirmation.';
      },

    login_email_label ()
      {
        return Template.instance ().passwordMode.get () ? 'E-mail' : 'Mon adresse mail :';
      },

    login_password_visible ()
      {
        return Template.instance ().passwordMode.get ();
      },

    login_disabled_attr ()
      {
        return Template.instance ().loginBusy.get () ? 'disabled' : null;
      },

    login_button_label ()
      {
        if (Template.instance ().loginBusy.get ()) return 'Envoi...';
        return Template.instance ().passwordMode.get () ? 'Se connecter' : 'Envoyer';
      },

    login_toggle_label ()
      {
        return Template.instance ().passwordMode.get ()
          ? 'Connexion par e-mail simple'
          : 'Cliquez ici si vous avez déjà un mot de passe';
      },
  });

Template.diffusion_login.events (
  {
    'submit .js_login_form' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        pTpl.loginMessage.set (null);
        pTpl.loginBusy.set (true);
        const tEmail = pTpl.find ('.js_login_email');
        const tPassword = pTpl.find ('.js_login_password');
        const tOptin = pTpl.find ('.js_optin_prospection');
        if (tOptin) enregistre_optin_prospection (!!tOptin.checked);
        if (!pTpl.passwordMode.get ())
          {
            demande_lien_passwordless_meteor (
              tEmail && tEmail.value,
              version_id_route (),
              chemin_retour_passwordless_courant (),
              (pErr, pRes) =>
                {
                  pTpl.loginBusy.set (false);
                  if (pErr)
                    pTpl.loginMessage.set (
                      {type : 'erreur', texte : message_erreur_diffusion (pErr, 'Envoi impossible.')}
                    );
                  else if (pRes && pRes.delivery && pRes.delivery.mode === 'local-dev')
                    pTpl.loginMessage.set (message_passwordless_envoye (pRes));
                  else
                    affiche_dialogue_passwordless_envoye ();
                }
            );
            return;
          }
        Meteor.loginWithPassword (
          tEmail && tEmail.value,
          tPassword && tPassword.value,
          (pErr) =>
            {
              pTpl.loginBusy.set (false);
              if (pErr)
                {
                  pTpl.loginMessage.set (
                    {type : 'erreur', texte : message_erreur_diffusion (pErr, 'Connexion impossible.')}
                  );
                  return;
                }
              const tVersionId = version_id_route () || version_id_entree_jeu_defaut ();
              const tReturnPath = chemin_entree_passwordless_diffusion (tVersionId, '/');
              gAuthSuccessMessage = 'Connexion réussie';
              assure_session_cogibulle_apres_login (
                {version_id : tVersionId, return_path : tReturnPath, success_message : 'Connexion réussie'}
              );
            }
        );
      },

    'click .js_login_password_toggle' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        pTpl.loginMessage.set (null);
        pTpl.passwordMode.set (!pTpl.passwordMode.get ());
      },

    'click .js_login_forgot_password' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        const tEmail = pTpl.find ('.js_login_email');
        const tAdresse = String (tEmail && tEmail.value || '').trim ().toLowerCase ();
        if (!tAdresse)
          {
            pTpl.loginMessage.set ({type : 'erreur', texte : 'Indiquez votre adresse e-mail avant de demander la réinitialisation.'});
            return;
          }
        pTpl.loginBusy.set (true);
        pTpl.loginMessage.set (null);
        Accounts.forgotPassword ({email : tAdresse}, (pErr) =>
          {
            pTpl.loginBusy.set (false);
            if (pErr)
              {
                pTpl.loginMessage.set (
                  {type : 'erreur', texte : message_erreur_compte (pErr, 'E-mail de réinitialisation non envoyé.')}
                );
                return;
              }
            pTpl.loginMessage.set ({type : 'succes', texte : 'Un e-mail de réinitialisation vient d’être envoyé.'});
          });
      },
  });

Template.diffusion_reset_password.onCreated (function ()
  {
    this.resetMessage = new ReactiveVar (null);
    this.resetBusy = new ReactiveVar (false);
  });

Template.diffusion_reset_password.helpers (
  {
    reset_message ()
      {
        const tMessage = Template.instance ().resetMessage.get ();
        return tMessage && tMessage.texte;
      },

    reset_message_class ()
      {
        const tMessage = Template.instance ().resetMessage.get ();
        return tMessage && tMessage.type === 'succes' ? 'form_success' : 'form_error';
      },

    reset_busy_attr ()
      {
        return Template.instance ().resetBusy.get () ? 'disabled' : null;
      },
  });

Template.diffusion_reset_password.events (
  {
    'submit .js_reset_password_form' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        const tPassword = pTpl.find ('.js_reset_new_password');
        const tConfirm = pTpl.find ('.js_reset_confirm_password');
        const tNouveau = tPassword && tPassword.value || '';
        if (!mot_de_passe_compte_valide (tNouveau))
          {
            pTpl.resetMessage.set ({type : 'erreur', texte : 'Le mot de passe doit contenir au moins 8 caractères.'});
            return;
          }
        if (tNouveau !== (tConfirm && tConfirm.value || ''))
          {
            pTpl.resetMessage.set ({type : 'erreur', texte : 'La confirmation ne correspond pas au mot de passe.'});
            return;
          }
        pTpl.resetBusy.set (true);
        pTpl.resetMessage.set (null);
        Accounts.resetPassword (Session.get ('reset_password_token'), tNouveau, (pErr) =>
          {
            pTpl.resetBusy.set (false);
            if (pErr)
              {
                gAuthSuccessMessage = null;
                pTpl.resetMessage.set (
                  {type : 'erreur', texte : message_erreur_compte (pErr, 'Lien de réinitialisation invalide ou expiré.')}
                );
                return;
              }
            const tVersionId = Session.get ('reset_password_version_id') || version_id_entree_jeu_defaut ();
            gAuthSuccessMessage = 'Mot de passe réinitialisé avec succès';
            gPasswordlessVersionId = tVersionId || gPasswordlessVersionId;
            gPasswordlessReturnPath = chemin_entree_passwordless_diffusion (tVersionId, '/');
            assure_session_cogibulle_apres_login (
              {
                version_id : tVersionId,
                success_message : 'Mot de passe réinitialisé avec succès',
              }
            );
          });
      },
  });

Template.diffusion_version_card.helpers (
  {
    catalogue_diapos_style ()
      {
        const tProjet = Projets.findOne ({_diffusion_version_id : this && this._id})
          || Projets.findOne ({_diffusion_game_id : this && this.game_id});
        const tPct = normalise_catalogue_diapos_taille_pct (
          tProjet && tProjet.catalogue_diapos_taille_pct
        );
        return `--catalogue-diapos-scale: ${tPct / 100};`;
      },

    titre ()
      {
        return this.manifest_summary && this.manifest_summary.titre || this.game_slug;
      },

    description ()
      {
        return this.manifest_summary && this.manifest_summary.description;
      },

    vignette_url ()
      {
        return url_asset_diffusion_protegee (
          this.manifest_summary && this.manifest_summary.vignette_url,
          this._id
        );
      },

    scenes ()
      {
        return this.manifest_summary && this.manifest_summary.scenes || 0;
      },

    cartes ()
      {
        return this.manifest_summary && this.manifest_summary.cartes || 0;
      },

    assets ()
      {
        return this.manifest_summary && this.manifest_summary.assets || 0;
      },

    access_label ()
      {
        return access_label (this.access_mode);
      },

    version_url ()
      {
        return chemin_entree_lecteur_diffusion (this._id);
      },
  });

Template.diffusion_lecteur.onCreated (function ()
  {
    this.accessError = new ReactiveVar (null);
    this.transitionActive = new ReactiveVar (false);
    this.cartesReussies = new ReactiveVar ({});
    this._accountOverlayOpenHandler = () => ouvre_overlay_compte_diffusion ();
    this._audioEntryActivateHandler = () =>
      {
        if (!this._lectureAudioPlayer) this._lectureAudioPlayer = create_lecture_audio_player ();
        this._lectureAudioPlayer.unlock ();
        deverrouille_audio_instant_lecture_scene ();
      };
    window.addEventListener ('diffusion:account-overlay-open', this._accountOverlayOpenHandler);
    window.addEventListener ('diffusion:audio-entry-activate', this._audioEntryActivateHandler);

    this.autorun (() =>
      {
        const tFormulaireIdentificationVisible = gAccessGateRendered.get ()
          && gAccessGateOpen.get ()
          && gAccessGateForm.get ();
        if (!tFormulaireIdentificationVisible)
          {
            this._identificationEmailFocused = false;
            return;
          }
        if (this._identificationEmailFocused) return;
        this._identificationEmailFocused = true;
        Tracker.afterFlush (() =>
          {
            const tEmail = this.find ('.js_identification_email');
            if (tEmail && typeof tEmail.focus === 'function') tEmail.focus ();
          });
      });

    this.autorun (() =>
      {
        const tVersion = version_courante ();
        demarre_session_publique (this, tVersion);
        demande_etat_acces_diffusion ();
        Session.set ('diffusion_version_id', tVersion && tVersion._id || null);

        const tVersionId = tVersion && tVersion._id || version_id_route ();
        if (!gVersionDataReady.get ()) return;
        const tSlug = scene_slug_active ();
        if (tSlug)
          {
            Session.set ('scene_slug', tSlug);
            remplace_url_scene_lecteur_si_necessaire (tVersionId, tSlug);
          }

        const tProjet = tVersion && Projets.findOne ({_diffusion_version_id : tVersion._id});
        const tScene = tVersion && scene_complete_client (tVersion._id, tSlug);
        if (!tProjet || !tScene) return;

        Session.set ('projet_id', tProjet._id);
        Session.set ('scene_id', tScene._id);
        prepare_audios_lecture_tpl (this, tProjet, tScene);
        const tPrechauffageKey = [
          tVersionId || "",
          tScene._id,
          session_id_version (tVersionId) || "",
          user_session_id () || "",
        ].join (":");
        if (this._preheatSceneKey !== tPrechauffageKey)
          {
            this._preheatSceneKey = tPrechauffageKey;
            prechauffe_assets_scene_lecture_diffusion (
              tScene._id,
              {
                maxUrls : 32,
                concurrence : 3,
              }
            ).catch (() => null);
          }
        Session.set ('projet_slugs',
          {
            user  : 'diffusion',
            projet: tProjet.slug || tVersion.game_slug || tProjet._id,
          });

        // Lecteur Pixi : le template scene_pixi se pilote seul depuis scene_id,
        // sans le mode lecture du template SVG.
        if (lecteur_pixi_route ()) return;

        if (this._hubLectureSceneId !== tScene._id)
          {
            this._hubLectureSceneId = tScene._id;
            Tracker.afterFlush (() =>
              {
                if (Session.get ('scene_id') === tScene._id)
                  demarre_lecture_scene_courante ();
              });
          }
      });
  });

Template.diffusion_lecteur.onDestroyed (function ()
  {
    if (this._accountOverlayOpenHandler)
      window.removeEventListener ('diffusion:account-overlay-open', this._accountOverlayOpenHandler);
    if (this._audioEntryActivateHandler)
      window.removeEventListener ('diffusion:audio-entry-activate', this._audioEntryActivateHandler);
    if (gAccountOverlayCloseTimer)
      {
        clearTimeout (gAccountOverlayCloseTimer);
        gAccountOverlayCloseTimer = null;
      }
    if (gAccessGateCloseTimer)
      {
        clearTimeout (gAccessGateCloseTimer);
        gAccessGateCloseTimer = null;
      }
    if (this._lectureAudioPlayer)
      {
        this._lectureAudioPlayer.dispose ();
        this._lectureAudioPlayer = null;
      }
    gAccountOverlayRendered.set (false);
    gAccountOverlayClosing.set (false);
    gAccessGateRendered.set (false);
    gAccessGateClosing.set (false);
    gAccessGateOpen.set (false);
    if (this._sceneTransitionAvantTimer) clearTimeout (this._sceneTransitionAvantTimer);
    if (this._sceneTransitionApresTimer) clearTimeout (this._sceneTransitionApresTimer);
    arrete_lecture_scene_courante ();
  });

Template.diffusion_lecteur.helpers (
  {
    lecteur_pixi_actif ()
      {
        return lecteur_pixi_route ();
      },

    lecteur_pixi_pret ()
      {
        charge_lecteur_pixi ();
        return gLecteurPixiCharge.get ();
      },

    version_introuvable ()
      {
        const tVersionId = version_id_route ();
        if (!tVersionId) return false;
        if (version_courante ()) return false;
        return DiffusionVersions.find ({active : true, status : 'active'}).count () > 0;
      },

    auth_ready ()
      {
        return gAuthReady.get ();
      },

    current_user ()
      {
        return gCurrentUser.get ();
      },

    reader_transition_class ()
      {
        const tTpl = Template.instance ();
        return tTpl && tTpl.transitionActive.get () ? 'is-active' : '';
      },

    reader_transition_style ()
      {
        const tFade = Math.max (0, Number (lecture_settings ().transition_scene_fade_ms) || 400);
        return `--reader-scene-transition-ms: ${tFade}ms;`;
      },

    account_overlay_rendered ()
      {
        return gAccountOverlayRendered.get ();
      },

    account_overlay_class ()
      {
        return gAccountOverlayClosing.get () ? 'is-closing' : 'is-open';
      },

    version_titre ()
      {
        const tVersion = version_courante ();
        return (tVersion && tVersion.manifest_summary && tVersion.manifest_summary.titre)
          || 'Accès requis';
      },

    acces_requis ()
      {
        const tVersion = version_courante ();
        if (!tVersion || tVersion.access_mode === 'public') return false;
        return !session_id_version (tVersion._id);
      },

    acces_password ()
      {
        const tPolicy = policy_courante ();
        return tPolicy && tPolicy.mode === 'password';
      },

    access_error ()
      {
        return Template.instance ().accessError.get ();
      },

    lecteur_charge ()
      {
        return hub_runtime_pret ();
      },

    hub_runtime_ready ()
      {
        return hub_runtime_pret ();
      },

    identification_dialog_open ()
      {
        return gAccessGateRendered.get ();
      },

    identification_dialog_class ()
      {
        if (gAccessGateClosing.get ()) return 'is-closing';
        return gAccessGateOpen.get () ? 'is-open' : '';
      },

    identification_title ()
      {
        const tTitre = gAccessGateTitle.get ();
        if (tTitre) return tTitre;
        const tNom = (communication_diffusion_courante ().editeur_nom || '').trim () || 'Cogibulle';
        return `Pour profiter de cette appli ${tNom} entièrement gratuite, il suffit d'une adresse mail`;
      },

    identification_title_class ()
      {
        return gAccessGateTitle.get () ? 'access_gate_title_expired' : '';
      },

    identification_message ()
      {
        const tMessage = gAccessGateMessage.get ();
        return tMessage && tMessage.texte;
      },

    identification_message_class ()
      {
        const tMessage = gAccessGateMessage.get ();
        return tMessage && tMessage.type === 'succes' ? 'form_success' : 'form_error';
      },

    identification_disabled_attr ()
      {
        return gAccessGateBusy.get () ? 'disabled' : null;
      },
  });

Template.diffusion_lecteur.events (
  {
    'submit .js_access_form' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        pTpl.accessError.set (null);

        const tVersionId = version_id_route ();
        const tPassword = pTpl.find ('.js_access_password');
        const tToken = pTpl.find ('.js_access_token');
        Meteor.call ('diffusion.access.start',
          {
            version_id: tVersionId,
            user_session_id: user_session_id (),
            password  : tPassword && tPassword.value,
            token     : tToken && tToken.value,
          },
          (pErr, pRes) =>
            {
              if (pErr)
                {
                  pTpl.accessError.set (message_erreur_diffusion (pErr, 'Accès refusé.'));
                  return;
                }
              definit_session_version (tVersionId, pRes.session_id);
          });
      },

    'click .js_account_overlay_close' ()
      {
        ferme_overlay_compte_diffusion ();
      },

    'click .js_account_overlay_panel' (pEvent)
      {
        pEvent.stopPropagation ();
      },

    'click .js_identification_cancel' ()
      {
        ferme_carte_active_diffusion ();
        ferme_identification_diffusion ();
      },

    'submit .js_identification_form' (pEvent, pTpl)
      {
        pEvent.preventDefault ();
        const tEmail = pTpl.find ('.js_identification_email');
        const tOptin = pTpl.find ('.js_optin_prospection');
        if (tOptin) enregistre_optin_prospection (!!tOptin.checked);
        gAccessGateBusy.set (true);
        gAccessGateMessage.set (null);
        demande_lien_passwordless_meteor (
          tEmail && tEmail.value,
          version_id_route (),
          chemin_retour_passwordless_courant (),
          (pErr, pRes) =>
            {
              gAccessGateBusy.set (false);
            if (pErr)
              {
                gAccessGateMessage.set (
                  {type : 'erreur', texte : message_erreur_diffusion (pErr, 'Envoi impossible.')}
                );
                return;
              }
              gere_resultat_envoi_passwordless_identification (pRes);
            }
        );
      },

  });

if (typeof document !== 'undefined')
  {
    document.addEventListener ('keydown', (pEvent) =>
      {
        if (pEvent.key === 'Escape' && gAccessGateOpen.get ())
          {
            ferme_carte_active_diffusion ();
            ferme_identification_diffusion ();
          }
        if (pEvent.key === 'Escape' && gDialogueEffaceLocalOuvert.get ())
          gDialogueEffaceLocalOuvert.set (false);
        // Ctrl+Option+L : purge des données locales après confirmation.
        // pEvent.code plutôt que pEvent.key : sur Mac, Option+L produit « ¬ ».
        if (pEvent.ctrlKey && pEvent.altKey && !pEvent.metaKey && pEvent.code === 'KeyL')
          {
            pEvent.preventDefault ();
            gDialogueEffaceLocalOuvert.set (true);
          }
      });
  }
